<template>
  <div class="min-h-screen bg-gray-50 py-8 relative">
    <!-- Centralized Loading Overlay -->
    <div 
      v-if="isPageLoading" 
      class="fixed inset-0 bg-white bg-opacity-80 flex items-center justify-center z-50"
    >
      <div class="flex flex-col items-center gap-3">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <div class="text-lg font-medium text-gray-700">
          Loading<span class="loading-dots"></span>
        </div>
      </div>
    </div>
    
    <div 
      class="mx-auto px-4 sm:px-5 lg:px-6 max-w-[1600px] transition-opacity duration-300"
      :class="{ 'opacity-50 pointer-events-none': isPageLoading }"
    >
      <!-- Header -->
      <div class="mb-8">
        <!-- Curve/Day Toggle -->
        <div class="flex justify-center mb-4">
          <div class="flex items-center gap-2">
            <span :class="['text-sm font-medium', viewMode === 'curve' ? 'text-blue-600' : 'text-gray-400']">Curve</span>
            <button
              @click="toggleViewMode"
              :class="[
                'relative inline-flex h-7 w-12 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2',
                viewMode === 'curve' 
                  ? 'bg-blue-600 focus:ring-blue-500' 
                  : 'bg-purple-600 focus:ring-purple-500'
              ]"
            >
              <span
                :class="[
                  'inline-block h-5 w-5 transform rounded-full bg-white transition-transform',
                  viewMode === 'curve' ? 'translate-x-1' : 'translate-x-6'
                ]"
              ></span>
            </button>
            <span :class="['text-sm font-medium', viewMode === 'day' ? 'text-purple-600' : 'text-gray-400']">Day</span>
          </div>
        </div>
        
        <!-- Dynamic Title -->
        <div class="flex justify-center mb-1">
          <Title align="center">{{ viewMode === 'curve' ? 'Curve Explorer' : 'Day Explorer' }}</Title>
        </div>
        
        
        <!-- Subject and Curve Info -->
        <div class="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-sm">
          <span class="text-gray-500">Subject</span>
          <span class="px-2 py-0.5 rounded-full bg-gray-100 text-gray-900 font-medium">{{ configuredData?.subid || 'N/A' }}</span>
          <span class="text-gray-300">•</span>
          <span class="text-gray-500">Dataset</span>
          <span class="px-2 py-0.5 rounded-full bg-gray-100 text-gray-900 font-medium">{{ configuredData?.dataset_identifier || '001' }}</span>
          <span class="text-gray-300">•</span>
          <span class="text-gray-500">Detected Curves</span>
          <span class="px-2 py-0.5 rounded-full bg-gray-100 text-gray-900 font-medium">{{ processingResults?.curve_features?.length || 0 }}</span>
          <span class="text-gray-300">•</span>
          <span class="text-gray-500">Days</span>
          <span class="px-2 py-0.5 rounded-full bg-gray-100 text-gray-900 font-medium">{{ dayCount }}</span>
        </div>
      </div>

        
        <!-- Timeline Component - Only render when we have valid timeline data -->
        <TimelineComponent
          v-if="processingResults && timelineStart && timelineEnd && chartCreationComplete"
          :earliest-time="earliestTime"
          :latest-time="latestTime"
          :curve-features="processingResults?.curve_features || []"
          :curve-timeline-map="curveTimelineMap"
          :day-data="dayData"
          :view-mode="viewMode"
          :selected-day-index="selectedDayIndex"
          :start-time="timelineStart"
          :end-time="timelineEnd"
          :max-time-range-hours="maxTimeRangeHours"
          :is-interactive="isPageReadyForInteraction"
          :is-frozen="isInTransitionalState"
          :freeze-message="unifiedStatusMessage"
          :allow-initialization-events="chartCreationComplete"
          :show-controls="true"
          :show-details="true"
          :alignment-label="timelineAlignmentLabel"
          :alignment-class="timelineAlignmentClass"
          :selected-curve-index="selectedCurveIndex"
          :current-curve-flags="currentCurveFlags"
          :session-id="configuredData?.session_id"
          @timeline-change="handleTimelineChange"
          @curve-selection-change="handleCurveSelectionChange"
          @day-selection-change="handleDaySelectionChange"
          @curve-relabeled="handleCurveRelabeled"
          @curve-relabel-error="handleCurveRelabelError"
          @curve-relabel-backend-error="handleCurveRelabelBackendError"
        />
        
        <!-- Debug info for timeline readiness -->
        <div v-if="earliestTime && latestTime && processingResults && !isTimelineDataReady" class="text-sm text-gray-500 p-2">
          Timeline not ready: {{ !timelineStart ? 'start missing' : '' }}{{ !timelineStart && !timelineEnd ? ' and ' : '' }}{{ !timelineEnd ? 'end missing' : '' }}
        </div>
        

        

        
        <!-- Status messages are now handled entirely by TimelineComponent -->
      </div>

      <!-- Signal Processing Chart -->
      <div class="bg-white rounded-lg shadow p-6 mb-6 border-2 border-gray-400 mx-4 mt-4">
        <!-- Chart Header with Toggle -->
        <button 
          @click="toggleSignalChartExpansion" 
          class="w-full flex items-center justify-between mb-4 bg-gray-50 hover:bg-gray-100 transition-colors rounded-lg p-4 border border-gray-200"
        >
          <div class="flex items-center">
            <h3 class="text-lg font-medium text-gray-900">Signal Processing Chart</h3>
          </div>
          <svg 
            class="w-5 h-5 text-gray-500 transition-transform duration-200" 
            :class="isSignalChartExpanded ? 'rotate-180' : ''"
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        
        <!-- Collapsible Chart Content -->
        <div v-if="isSignalChartExpanded" class="relative">
          <div :style="{ height: signalChartHeight + 'px' }" class="relative">
            <SignalProcessingChart
              ref="signalChart"
              :filtered-data="filteredData || []"
              :curve-features="processingResults?.curve_features || []"
              :selected-curve-index="selectedCurveIndex"
              header-title=""
              :timeline-start="timelineStart || ''"
              :timeline-end="timelineEnd || ''"
              :curve-timeline-map="curveTimelineMap"
              :auto-update="true"
            />
          </div>
          <!-- Y-axis Resize Handle (transparent) -->
          <div 
            class="absolute bottom-0 left-0 right-0 h-3 bg-transparent cursor-ns-resize z-10"
            @mousedown="startResizeSignalChart"
            title="Drag to resize chart height"
          >
          </div>
        </div>
      </div>

      <!-- Device Removal Chart -->
      <div class="bg-white rounded-lg shadow p-6 mb-6 border-2 border-gray-400 mx-4">
        <!-- Chart Header with Toggle -->
        <button 
          @click="toggleNonWearDetectionChartExpansion" 
          class="w-full flex items-center justify-between mb-4 bg-gray-50 hover:bg-gray-100 transition-colors rounded-lg p-4 border border-gray-200"
        >
          <div class="flex items-center">
            <h3 class="text-lg font-medium text-gray-900">Device Removal Detection</h3>
          </div>
          <svg 
            class="w-5 h-5 text-gray-500 transition-transform duration-200" 
            :class="isNonWearDetectionChartExpanded ? 'rotate-180' : ''"
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        
        <!-- Collapsible Chart Content -->
        <div v-if="isNonWearDetectionChartExpanded" class="relative">
          <div :style="{ height: nonWearChartHeight + 'px' }" class="pb-4">
            <NonWearDetectionChart
              ref="deviceChart"
              :filtered-data="filteredData || []"
              :curve-features="processingResults?.curve_features || []"
              :selected-curve-index="selectedCurveIndex"
              :timeline-start="timelineStart || ''"
              :timeline-end="timelineEnd || ''"
              :curve-timeline-map="curveTimelineMap"
              :auto-update="true"
              :show-controls="true"
              :header-title="''"
              @chart-ready="onNonWearDetectionChartReady"
              @chart-updated="onNonWearDetectionChartUpdated"
              @chart-error="onNonWearDetectionChartError"
            />
          </div>
          <!-- Y-axis Resize Handle (transparent) -->
          <div 
            class="absolute bottom-0 left-0 right-0 h-3 bg-transparent cursor-ns-resize z-10"
            @mousedown="startResizeNonWearChart"
            title="Drag to resize chart height"
          >
          </div>
        </div>
      </div>
  </div>
</template>

<script>
import { Chart, registerables } from 'chart.js'
import 'chartjs-adapter-date-fns'
import axios from 'axios'
import { buildApiUrl, API_ENDPOINTS, normalizeWindowRows, normalizeTimelineRange } from '../../config/api.js'
import TimelineComponent from '../../components/TimelineComponent.vue'
import { SignalProcessingChart, NonWearDetectionChart } from '../../components'
import Subheader from '../../components/Subheader.vue'
import Title from '../../components/Title.vue'
import { ALL_CHART_COLUMNS, createDataCache } from '../../utils/chartDataColumns.js'
// Removed annotation plugin import - no longer using chartjs-plugin-annotation

// Register Chart.js components
Chart.register(...registerables)

export default {
  name: 'Explorer',
  inject: ['globalState', 'updateGlobalState', 'updateCurveFeatures', 'setBackButton', 'hideBackButton'],
  props: {
    initialCurveIndex: {
      type: [String, Number],
      default: 0,
      validator: function (value) {
        // Allow empty string, numbers, or string numbers
        return value === '' || !isNaN(Number(value))
      }
    }
  },
  components: {
    TimelineComponent,
    SignalProcessingChart,
    NonWearDetectionChart,
    Subheader,
    Title,
  },
  created() {

    try {
      // Add any critical initialization here

      
      // Force continue to next lifecycle hook if stuck
      setTimeout(() => {
        if (!this._mountedPhaseStarted) {

          this.$forceUpdate()
          
                // Try to manually trigger the next lifecycle hook
      setTimeout(() => {
        if (!this._mountedPhaseStarted) {

          // Force the component to continue by updating a reactive property
          this._forceContinue = true
          

        }
      }, 500)
        }
      }, 1000)
      
    } catch (error) {
      console.error('❌ Error in Explorer created():', error)
    }
  },
  data() {

    return {
      configuredData: null,
      processingResults: null,
      timelineStart: null,
      timelineEnd: null,
      deviceChart: null,
      viewMode: 'curve', // 'curve' or 'day' - Track the current view mode
      selectedCurveIndex: '', // Track selected curve for dropdown
      previousCurveIndex: '', // Remember the last selected curve before switching to day mode
      dayData: [], // Day-level data for day selection
      selectedDayIndex: '', // Track selected day for dropdown
      previousDayIndex: '', // Remember the last selected day before switching to curve mode
      // Chart toggle states - default to open
      isSignalChartExpanded: true,
      isNonWearDetectionChartExpanded: true,
      // Chart height states for y-axis resizing
      signalChartHeight: 600, // Default height in px
      nonWearChartHeight: 420, // Default height in px
      isResizingSignalChart: false,
      isResizingNonWearChart: false,
      resizeStartY: null,
      resizeStartHeight: null,
      resizeMouseMoveHandler: null,
      resizeMouseUpHandler: null,
      updateChartsTimeout: null, // Add debounce timeout for chart updates
      isUpdatingCharts: false,
      chartUpdateInProgress: false, // Prevent multiple simultaneous chart updates
      pendingChartUpdate: false, // Track if an update is queued
      freezeStateCheckInterval: null, // Interval for checking freeze state consistency
      chartCreationComplete: false, // Track if chart creation is complete
      _settingInitialTimeline: false, // Flag to prevent reactive updates during initial timeline setup
      lastChartUpdateTime: 0, // Track last chart update time for throttling
      chartUpdateThrottleMs: 1000, // Minimum time between chart updates (1 second)
      chartOperationTimeout: null, // Global timeout for chart operations
      maxChartOperationTime: 5000, // Maximum time for any chart operation (5 seconds)
      chartUpdateSafetyTimer: null, // Safety timer for stuck chart updates
      
      // Recovery state tracking
      _recoveringFromContextError: false,
      
      // Chart health monitoring
      chartHealthCheckInterval: null,
      _healthCheckCount: 0, // Counter for health check logging
      
      // UI interaction guards (checkbox moved to SignalProcessingChart)
      _checkboxChangeInProgress: false,
      _checkboxProcessingStatus: null,
      
      hideRawLowQualityWithImputation: false,
      showTooltip: false,
      isTimelineAdjusting: false, // Track when timeline is being adjusted

            // New properties for server-side data fetching
      currentTimeRangeData: null, // Data for current time range
      isFetchingData: false, // Track if we're currently fetching data
      lastFetchTime: null, // Track when we last fetched data
      fetchDebounceTimeout: null, // Debounce data fetching
      maxTimeRangeHours: 24, // Maximum time range allowed (24 hours)
      serverStatus: 'unknown', // Track server connection status
      _updatingXAxis: false, // Recursion guard for x-axis updates
      datasetTimelineRange: null, // Stores the full dataset range (earliest/latest timestamps)
      
      // Timeline selection percentages (for slider markers)
      selectedRangePercent: { start: 0, end: 100 },
      
      // Custom dropdown state
      showCurveDropdown: false, // Control curve dropdown visibility
      
      // Component initialization state
      _componentInitialized: false, // Track if component has completed initial setup
      _mountedPhaseStarted: false, // Track if mounted phase has started
      _forceContinue: false, // Force continue flag for stuck components
      _beforeMountReached: false, // Track if beforeMount was reached
      _forceMounted: false, // Force mounted flag for stuck components
      _isUnmounting: false, // Track if component is unmounting to prevent operations
      
      // Data cache for time range fetches (Phase 1 optimization)
      timeRangeDataCache: createDataCache(5)  // Cache up to 5 time ranges

    }
  },
  watch: {
    // Debug watcher to track when curve features change
    'processingResults.curve_features': {
      handler(newFeatures, oldFeatures) {
        console.log('🔄 Explorer: curve_features changed', {
          newCount: newFeatures?.length || 0,
          oldCount: oldFeatures?.length || 0,
          hasOverrides: newFeatures?.some(c => c.override_flags === 1) || false,
          overrideCount: newFeatures?.filter(c => c.override_flags === 1).length || 0
        })
      },
      deep: true
    },
    // Watch for route query changes to update back button
    '$route.query': {
      handler(newQuery, oldQuery) {
        // Only update if the 'from' parameter changed
        if (newQuery.from !== oldQuery?.from) {
          console.log('🔄 Route query changed, updating back button')
          this.setupBackButton()
        }
      },
      immediate: false
    }
  },
  computed: {
    currentCurveFlags() {
      // Collect any FLAG_* keys on the curve row
      const curveIndex = this.selectedCurveIndex !== '' ? parseInt(this.selectedCurveIndex) : 0
      const curve = this.processingResults?.curve_features?.[curveIndex] || null
      if (!curve) return []
      const entries = Object.entries(curve)
      const flags = entries
        .filter(([key, value]) => {
          // Always exclude FLAG_override_decision when override_flags = 0 (threshold-based assessment)
          if (key === 'FLAG_override_decision' && curve.override_flags === 0) {
            return false
          }
          // Standard flag filtering: starts with FLAG_, not null, value equals 1
          return key.startsWith('FLAG_') && value != null && Number(value) === 1
        })
        .map(([key]) => ({ key, label: this.formatFlagLabel(key) }))
      return flags
    },
    getCurrentCurveValid() {
      const curveIndex = this.selectedCurveIndex !== '' ? parseInt(this.selectedCurveIndex) : 0
      const curve = this.processingResults?.curve_features?.[curveIndex] || null
      if (!curve) return null
      const v = curve.CURVE_VALID
      if (v == null) return null
      return Number(v) === 1
    },
    dayCount() {
      // Compute day count the same way as SubjectDashboard.vue
      return this.configuredData?.dayCount || 0
    },
    // Computed property to check if page is in a static state (no operations in progress)
    isPageStatic() {
      return !this.isInTransitionalState; // Use centralized transitional state
    },
    
    // Computed property to check if page is ready for user interactions
    isPageReadyForInteraction() {
      // Page is ready if charts are complete and no actual operations are in progress
      // Checkbox processing status doesn't block interactions
      return this.chartCreationComplete && 
             !this.isUpdatingCharts && 
             !this.chartUpdateInProgress && 
             !this.isFetchingData && 
             !this._settingInitialTimeline;
    },
    
    // Deprecated checkbox controls are removed from here

    

    // Single centralized property for all transitional/loading/updating states
    // Note: _checkboxProcessingStatus is excluded from blocking chart updates
    // It's only used for display purposes, not for blocking operations
    isInTransitionalState() {
      // Guard: Don't evaluate until component is properly initialized
      if (!this._componentInitialized) {
        return true // Return true during initialization to prevent early evaluation
      }
      
      // If charts are created and component is initialized, be more lenient
      if (this.chartCreationComplete && this._componentInitialized) {
        // Only check critical flags that should actually freeze the timeline
        const state = (
          this.isUpdatingCharts ||
          this.chartUpdateInProgress ||
          this._settingInitialTimeline
        )
        
        // Debug logging for post-creation state
        if (state) {
          console.log('🔍 isInTransitionalState is true (post-creation) due to:')
          console.log('  - isUpdatingCharts:', this.isUpdatingCharts)
          console.log('  - chartUpdateInProgress:', this.chartUpdateInProgress)
          console.log('  - _settingInitialTimeline:', this._settingInitialTimeline)
        }
        
        return state
      }
      
      // During initialization, check all flags
      const state = (
        this.isUpdatingCharts ||
        this.chartUpdateInProgress ||
        this.isFetchingData ||
        this._settingInitialTimeline ||
        !this.chartCreationComplete ||
        this.pendingChartUpdate ||
        this.updateChartsTimeout !== null
      )
      
      // Debug logging to track which condition is keeping the state true
      if (state) {
        console.log('🔍 isInTransitionalState is true due to:')
        console.log('  - isUpdatingCharts:', this.isUpdatingCharts)
        console.log('  - chartUpdateInProgress:', this.chartUpdateInProgress)
        console.log('  - isFetchingData:', this.isFetchingData)
        console.log('  - _settingInitialTimeline:', this._settingInitialTimeline)
        console.log('  - chartCreationComplete:', this.chartCreationComplete)
        console.log('  - pendingChartUpdate:', this.pendingChartUpdate)
        console.log('  - updateChartsTimeout:', !!this.updateChartsTimeout)
      }
      
      return state
    },
    
    // Check if timeline data is ready for rendering
    isTimelineDataReady() {
      // Simplified check to avoid circular dependencies during initialization
      const ready = (
        this.processingResults &&
        this.timelineStart &&
        this.timelineEnd
      )
      
      console.log('🔍 isTimelineDataReady check:', {
        ready,
        hasProcessingResults: !!this.processingResults,
        timelineStart: this.timelineStart,
        timelineEnd: this.timelineEnd,
        timelineStartType: typeof this.timelineStart,
        timelineEndType: typeof this.timelineEnd,
        timelineStartLength: this.timelineStart ? this.timelineStart.length : 0,
        timelineEndLength: this.timelineEnd ? this.timelineEnd.length : 0,
        processingResultsKeys: this.processingResults ? Object.keys(this.processingResults) : [],
        curveFeaturesLength: this.processingResults?.curve_features?.length || 0,
        curveFeatures: this.processingResults?.curve_features
      })
      
      return ready
    },
    
    // Centralized loading state for the entire page
    isPageLoading() {
      // Show loading when:
      // 1. Data is being fetched
      // 2. Data is not ready (no currentTimeRangeData or timeline not ready)
      // 3. Charts are not created yet
      const loading = (
        this.isFetchingData ||
        !this.currentTimeRangeData ||
        !this.isTimelineDataReady ||
        !this.chartCreationComplete
      )
      return loading
    },

    // Unified status message for all loading/updating states
    unifiedStatusMessage() {
      // Prioritize checkbox processing status for immediate feedback
      if (this._checkboxProcessingStatus !== null) {
        return this._checkboxProcessingStatus
      }
      
      // Use the centralized transitional state property
      if (this.isInTransitionalState) {
        return 'Updating ⟳'
      }
      return null // No status message needed
    },
    


    // Check if any status message should be shown
    shouldShowStatusMessage() {
      // Show status when in any transitional state OR when checkbox is being processed
      const shouldShow = this.isInTransitionalState || this._checkboxProcessingStatus !== null
      
      // Debug logging to track when this is evaluated
      if (shouldShow) {

      }
      
      return shouldShow
    },
    
    // Computed property to check if charts are busy
    isChartsBusy() {
      return this.isUpdatingCharts || this.chartUpdateInProgress;
    },
    
    // Computed property to check if timeline is busy
    isTimelineBusy() {
      return this._settingInitialTimeline;
    },
    
    // Computed property to filter data based on timeline selection
    filteredData() {
      try {

        
        // Use server-fetched data instead of full dataset from storage
        if (!this.currentTimeRangeData?.length) {

          return []
        }
        
        // Safety check: if timeline dates aren't set yet, return full fetched data
        if (!this.timelineStart || !this.timelineEnd) {

          return this.currentTimeRangeData
        }
        
        const data = this.currentTimeRangeData

        
        const startDate = new Date(this.timelineStart)
        const endDate = new Date(this.timelineEnd)
        
        if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
          console.warn('Invalid timeline dates:', { start: this.timelineStart, end: this.timelineEnd })
          return data
        }
        
        // CRITICAL: Check if timeline has valid order
        if (startDate >= endDate) {
          console.error('❌ Invalid timeline order in filteredData:', {
            start: startDate.toISOString(),
            end: endDate.toISOString(),
            timelineStart: this.timelineStart,
            timelineEnd: this.timelineEnd
          })
          
          // Don't modify reactive properties in computed properties - just return data
          console.warn('⚠️ Timeline order invalid, returning unfiltered data')
          return data
        }
        
        const filtered = data.filter(row => {
          try {
            const tMs = row.tMs != null ? row.tMs : (row.datetime ? new Date(row.datetime).getTime() : NaN)
            if (isNaN(tMs)) {
              console.warn('Invalid datetime found:', row.datetime)
              return false
            }
            const isInRange = tMs >= startDate.getTime() && tMs <= endDate.getTime()
            return isInRange
          } catch (error) {
            console.error('Error filtering row:', error, row)
            return false
          }
        })
        

        
        if (filtered.length === 0) {
          console.warn('WARNING: No data remains after filtering!')
        }
        
        return filtered
      } catch (error) {
        console.warn('Error in filteredData computed property:', error)
        return []
      }
    },
    filteredDataLength() {
      try {
        return this.filteredData.length
      } catch (error) {
        console.warn('Error in filteredDataLength computed property:', error)
        return 0
      }
    },
    
    // Computed property to show current timeline status
    timelineStatusMessage() {
      if (!this.processingResults?.curve_features?.length) {
        // Check if original dataset is > 24 hours to determine message
        if (this.fullDatasetEarliestTime && this.fullDatasetLatestTime) {
          const fullStart = new Date(this.fullDatasetEarliestTime)
          const fullEnd = new Date(this.fullDatasetLatestTime)
          const fullDuration = (fullEnd.getTime() - fullStart.getTime()) / (1000 * 60 * 60)
          
          if (fullDuration > 24) {
            // Original dataset > 24 hours, so we're showing first 24 hours
            return 'No curves detected - showing first 24 hours'
          } else {
            // Original dataset ≤ 24 hours, so we're showing full range
            return 'No curves detected - using full data range'
          }
        }
        return 'No curves detected - using full data range'
      }
      
      // Check if timeline is showing full range or curve-focused view
      if (this.fullDatasetEarliestTime && this.fullDatasetLatestTime) {
        const fullStart = new Date(this.fullDatasetEarliestTime)
        const fullEnd = new Date(this.fullDatasetLatestTime)
        const timelineStart = new Date(this.timelineStart)
        const timelineEnd = new Date(this.timelineEnd)
        
        // If timeline covers full range (within 1 minute tolerance), show full range message
        const startDiff = Math.abs(timelineStart.getTime() - fullStart.getTime())
        const endDiff = Math.abs(timelineEnd.getTime() - fullEnd.getTime())
        const tolerance = 60 * 1000 // 1 minute
        
        if (startDiff <= tolerance && endDiff <= tolerance) {
          return 'Timeline shows full data range (24-hour limit enforced)'
        } else {
          return 'Timeline focused on selected curve ±1 hour'
        }
      }
      
      return 'Timeline focused on selected curve ±1 hour'
    },
    
    timeRangeHours() {
      try {
        if (!this.timelineStart || !this.timelineEnd) {
          if (this.currentTimeRangeData?.length === 0) return 0
          const first = new Date(this.currentTimeRangeData[0].datetime)
          const last = new Date(this.currentTimeRangeData[this.currentTimeRangeData.length - 1].datetime)
          return (last - first) / (1000 * 60 * 60)
        }
        const start = new Date(this.timelineStart)
        const end = new Date(this.timelineEnd)
        return (end - start) / (1000 * 60 * 60)
      } catch (error) {
        console.warn('Error in timeRangeHours computed property:', error)
        return 0
      }
    },
    samplingRate() {
      try {
        // Try to get sampling rate from configuredData or from processingResults if stored there
        const rate = this.configuredData?.finalSamplingRate || 
                    this.processingResults?.configuredData?.finalSamplingRate || 1

        return rate
      } catch (error) {
        console.warn('Error in samplingRate computed property:', error)
        return 1
      }
    },
    
    // Computed properties for timeline component - always show full dataset range
    earliestTime() {
      // Priority 1: Use the fetched timeline range from API (full dataset range)
      if (this.datasetTimelineRange?.earliest) {

        return this.datasetTimelineRange.earliest
      }
      
      // Priority 2: Fallback to stored metadata
      if (this.processingResults?.data_metadata?.start_time) {

        return this.processingResults.data_metadata.start_time
      }
      

      return null
    },
    
    latestTime() {
      // Priority 1: Use the fetched timeline range from API (full dataset range)
      if (this.datasetTimelineRange?.latest) {

        return this.datasetTimelineRange.latest
      }
      
      // Priority 2: Fallback to stored metadata
      if (this.processingResults?.data_metadata?.end_time) {

        return this.processingResults.data_metadata.end_time
      }
      

      return null
    },
    
    // Computed properties for full dataset range (used for timeline expansion)
    fullDatasetEarliestTime() {
      if (this.datasetTimelineRange?.earliest) {
        return this.datasetTimelineRange.earliest
      }
      
      if (this.processingResults?.data_metadata?.start_time) {
        return this.processingResults.data_metadata.start_time
      }
      
      return null
    },
    
    fullDatasetLatestTime() {
      if (this.datasetTimelineRange?.latest) {
        return this.datasetTimelineRange.latest
      }
      
      if (this.processingResults?.data_metadata?.end_time) {
        return this.processingResults.data_metadata.end_time
      }
      
      return null
    },
    
    // Whether current timeline exactly aligns with the current curve's start/end
    isTimelineAlignedWithCurrentCurve() {
      try {
        const curveIndex = this.selectedCurveIndex !== '' ? parseInt(this.selectedCurveIndex) : 0
        const curve = this.curveTimelineMap && this.curveTimelineMap[curveIndex]
        if (!curve || !this.timelineStart || !this.timelineEnd) return false
        
        const normalize = (s) => typeof s === 'string' ? s.replace(/\.\d{3}/, '').replace(/Z$/, '') : s
        const tStart = normalize(this.timelineStart)
        const tEnd = normalize(this.timelineEnd)
        const cStart = normalize(curve.start)
        const cEnd = normalize(curve.end)
        
        if (tStart === cStart && tEnd === cEnd) return true
        
        // Fallback: check within 60s tolerance to avoid millisecond/zone drift
        const toMs = (v) => {
          if (!v) return NaN
          const d = new Date(typeof v === 'string' && !/Z$/.test(v) ? v : String(v))
          return d.getTime()
        }
        const tol = 60 * 1000
        const dStart = Math.abs(toMs(tStart) - toMs(cStart))
        const dEnd = Math.abs(toMs(tEnd) - toMs(cEnd))
        return dStart <= tol && dEnd <= tol
      } catch (_) {
        return false
      }
    },
    
    // Whether current timeline is the standard curve-focused ±1 hour view
    isTimelineCurvePaddedView() {
      try {
        const curveIndex = this.selectedCurveIndex !== '' ? parseInt(this.selectedCurveIndex) : 0
        const curve = this.curveTimelineMap && this.curveTimelineMap[curveIndex]
        if (!curve || !this.timelineStart || !this.timelineEnd) return false
        
        const toMs = (s) => new Date(typeof s === 'string' && !/Z$/.test(s) ? s : String(s)).getTime()
        const startMs = toMs(this.timelineStart)
        const endMs = toMs(this.timelineEnd)
        const cStartMs = toMs(curve.start)
        const cEndMs = toMs(curve.end)
        const oneHour = 60 * 60 * 1000
        const tol = 2 * 60 * 1000 // 2 minutes tolerance
        
        return Math.abs((cStartMs - startMs) - oneHour) <= tol && Math.abs((endMs - cEndMs) - oneHour) <= tol
      } catch (_) {
        return false
      }
    },
    timelineAlignmentLabel() {
      // Check if we're in day mode and have a selected day
      if (this.selectedDayIndex !== '' && this.dayData && this.dayData.length > 0) {
        const dayIndex = parseInt(this.selectedDayIndex)
        const selectedDay = this.dayData[dayIndex]
        if (selectedDay) {
          // Check if timeline is still aligned with the selected day's range
          if (this.isTimelineAlignedWithSelectedDay) {
            // Show the selected day's date
            const dayDate = new Date(selectedDay.begin_day || selectedDay.start_time || selectedDay.date || new Date())
            return dayDate.toLocaleDateString('en-US', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })
          } else {
            // Timeline has been manually adjusted, show Custom Range
            return 'Custom Range'
          }
        }
      }
      
      // Default timeline alignment labels
      if (this.isTimelineAlignedWithCurrentCurve) return 'Aligned with Curve'
      if (this.isTimelineCurvePaddedView) return 'Curve ±1h View'
      return 'Custom Range'
    },
    timelineAlignmentClass() {
      // Check if we're in day mode and have a selected day
      if (this.selectedDayIndex !== '' && this.dayData && this.dayData.length > 0) {
        const dayIndex = parseInt(this.selectedDayIndex)
        const selectedDay = this.dayData[dayIndex]
        if (selectedDay) {
          // Check if timeline is still aligned with the selected day's range
          if (this.isTimelineAlignedWithSelectedDay) {
            // Different style for day mode
            return 'bg-purple-50 text-purple-700 border-purple-200'
          } else {
            // Timeline has been manually adjusted, use Custom Range style
            return 'bg-amber-50 text-amber-700 border-amber-200'
          }
        }
      }
      
      // Default timeline alignment classes
      if (this.isTimelineAlignedWithCurrentCurve) return 'bg-green-50 text-green-700 border-green-200'
      if (this.isTimelineCurvePaddedView) return 'bg-blue-50 text-blue-700 border-blue-200'
      return 'bg-amber-50 text-amber-700 border-amber-200'
    },
    
    // Check if timeline is aligned with the currently selected day's range
    isTimelineAlignedWithSelectedDay() {
      if (this.selectedDayIndex === '' || !this.dayData || !this.dayData.length || !this.timelineStart || !this.timelineEnd) {
        return false
      }
      
      try {
        const dayIndex = parseInt(this.selectedDayIndex)
        const selectedDay = this.dayData[dayIndex]
        if (!selectedDay) return false
        
        const dayStart = new Date(selectedDay.begin_day || selectedDay.start_time || selectedDay.date)
        const dayEnd = new Date(selectedDay.end_day || selectedDay.end_time || selectedDay.date)
        const timelineStart = new Date(this.timelineStart)
        const timelineEnd = new Date(this.timelineEnd)
        
        // Allow for small tolerance (1 minute) to account for rounding differences
        const tolerance = 60 * 1000 // 1 minute in milliseconds
        
        const startDiff = Math.abs(timelineStart.getTime() - dayStart.getTime())
        const endDiff = Math.abs(timelineEnd.getTime() - dayEnd.getTime())
        
        return startDiff <= tolerance && endDiff <= tolerance
      } catch (error) {
        console.warn('Error checking day alignment:', error)
        return false
      }
    },
    
    // Easy-to-reference curve timeline mapping
    curveTimelineMap() {
      if (!this.processingResults?.curve_features?.length) {
        return {}
      }
      
      const curveMap = {}
      this.processingResults.curve_features.forEach((curve, index) => {
        const curveId = curve.curve_id || index
        // API returns curve times in simple format: '2021-05-22T15:51:00' (no timezone conversion)
        // Create Date objects directly from the raw timestamps
        const startDate = new Date(curve.begin_CURVE)
        const endDate = new Date(curve.end_CURVE)
        
        curveMap[curveId] = {
          id: curveId,
          index: index,
          
          // Raw datetime strings (for API calls, storage, etc.)
          start: curve.begin_CURVE,
          end: curve.end_CURVE,
          
          // Date objects (for calculations, comparisons, arithmetic)
          startDate: startDate,
          endDate: endDate,
          
          // Pre-formatted display strings (for UI display)
          startFormatted: this.formatDateTimeDisplay(curve.begin_CURVE),
          endFormatted: this.formatDateTimeDisplay(curve.end_CURVE),
          
          // Other curve properties
          duration: curve.duration_CURVE,
          peak: curve.peak_CURVE,
          riseDuration: curve.rise_duration_CURVE,
          riseRate: curve.rise_rate_CURVE,
          fallDuration: curve.fall_duration_CURVE,
          fallRate: curve.fall_rate_CURVE,
          threshold: curve.threshold || curve.unadjusted_threshold || 10,
          // Use REGION_VALID (combined curve + periphery validity) as the main quality indicator
          // Fallback to CURVE_VALID for backward compatibility
          valid: (curve?.REGION_VALID !== undefined && curve?.REGION_VALID !== null) 
            ? Number(curve.REGION_VALID) === 1 
            : Number(curve?.CURVE_VALID) === 1,
          // CRITICAL: Include override-related fields so isCurrentlyOverridden works correctly
          CURVE_VALID: curve.CURVE_VALID,
          REGION_VALID: curve.REGION_VALID, // Also include REGION_VALID for reference
          override_flags: curve.override_flags,
          FLAG_override_decision: curve.FLAG_override_decision,
          override_note: curve.override_note,
          threshold_based_quality: curve.threshold_based_quality
        }
      })
      
      // Log the curve mapping for debugging
      console.log('📊 curveTimelineMap computed:', {
        curveCount: Object.keys(curveMap).length,
        curves: curveMap,
        firstCurve: curveMap[0] ? {
          // Raw strings
          start: curveMap[0].start,
          end: curveMap[0].end,
          // Date objects
          startDate: curveMap[0].startDate,
          endDate: curveMap[0].endDate,
          // Formatted strings
          startFormatted: curveMap[0].startFormatted,
          endFormatted: curveMap[0].endFormatted
        } : null
      })
      
      return curveMap
    },
    
    // Helper to get curve by ID or index
    getCurveById(curveId) {
      return this.curveTimelineMap[curveId] || null
    },
    
    // Helper to get curve timeline info (start/end) by ID
    getCurveTimeline(curveId) {
      const curve = this.curveTimelineMap[curveId]
      if (!curve) return null
      
      return {
        start: curve.start,
        end: curve.end,
        startDate: curve.startDate,
        endDate: curve.endDate,
        duration: curve.duration
      }
    },

  },
  watch: {
    // Watch for changes in chart update state
    isUpdatingCharts(newVal, oldVal) {
      if (newVal && !oldVal) {
        // Force stop Chart.js operations
        this.forceStopChartJS()
      }
    },
    chartUpdateInProgress(newVal, oldVal) {
      if (newVal && !oldVal) {
        // Force stop Chart.js operations
        this.forceStopChartJS()
      }
    },
    // Watch for changes in curve selection to update chart styling
    selectedCurveIndex(newVal, oldVal) {
      if (newVal !== oldVal && this.chartCreationComplete) {
        // Only update charts if initial setup is complete

        // Use the new explicit update method instead of debouncedUpdateCharts
        this.onCurveSelectionChange({ curveIndex: newVal })
      } else if (newVal !== oldVal && !this.chartCreationComplete) {

      }
    },
    
    // Checkbox watchers removed; control moved inside SignalProcessingChart
    
    // Watch for changes in filteredData to force chart updates
    // DISABLED: This was causing infinite loops with chart updates
    /*
    filteredData: {
      handler(newData, oldData) {
        // Only run if component is fully initialized to prevent loops
        if (!this._componentInitialized || !this.chartCreationComplete) {
          console.log('🔄 filteredData watcher blocked - component not ready')
          return
        }
        
        if (newData && newData.length > 0) {
          console.log('📊 filteredData changed - charts will update automatically via Vue reactivity')
        }
      },
      deep: false // Disable deep watching to prevent infinite loops
    },
    */
    
    // Watch for force continue flag to manually trigger lifecycle progression
    _forceContinue(newVal) {
      if (newVal && !this._mountedPhaseStarted) {
        console.log('🔧 Force continue triggered, manually progressing lifecycle...')
        this._forceContinue = false // Reset flag
        // Try to manually trigger the next phase
        this.$nextTick(() => {
          if (!this._mountedPhaseStarted) {
            console.log('🔧 Manually calling beforeMount logic...')
            this.beforeMount()
          }
        })
      }
    },
    
    // Watch for force mounted flag to manually trigger mounted phase
    _forceMounted(newVal) {
      if (newVal && !this._mountedPhaseStarted) {
        console.log('🔧 Force mounted triggered, manually progressing to mounted...')
        this._forceMounted = false // Reset flag
        // Try to manually trigger the mounted phase
        this.$nextTick(() => {
          if (!this._mountedPhaseStarted) {
            console.log('🔧 Manually calling mounted logic...')
            try {
              this.mounted()
            } catch (error) {
              console.error('❌ Error manually calling mounted():', error)
            }
          }
        })
      }
    },
    
    // Watch for chart creation completion and force unfreeze timeline
    chartCreationComplete(newVal) {
      if (newVal) {
        console.log('🔧 Chart creation complete, ensuring timeline is unfrozen')
        // Use nextTick to ensure all reactive updates are complete
        this.$nextTick(() => {
          setTimeout(() => {
            if (this.isInTransitionalState) {
              console.log('🔧 Force unfreezing timeline after chart creation')
              this.forceResetTransitionalState()
            }
          }, 500) // Small delay to allow any pending operations to complete
        })
      }
    },
    
    // Watch for timeline changes to detect zoom out to full range
    timelineStart: {
      handler() {
        console.log('🔄 Timeline start changed:', this.timelineStart)
        this.updateChartXAxisLimits()
        
        // Check if this is a zoom out to full range
        this.handleTimelineZoomOut()
        
        // Only update charts if not currently dragging to prevent stack overflow
        if (!this.isTimelineAdjusting) {
          this.$nextTick(() => {
            this.debouncedUpdateCharts()
          })
        } else {
          console.log('🚫 Chart update skipped - timeline is being adjusted')
        }
      }
    },
    timelineEnd: {
      handler() {
        console.log('🔄 Timeline end changed:', this.timelineEnd)
        this.updateChartXAxisLimits()
        
        // Check if this is a zoom out to full range
        this.handleTimelineZoomOut()
        
        // If in day mode, auto-sync the day dropdown when the timeline exactly matches a day
        if (this.viewMode === 'day') {
          this.updateDaySelectionFromTimeline()
        }
        
        // Only update charts if not currently dragging to prevent stack overflow
        if (!this.isTimelineAdjusting) {
          this.$nextTick(() => {
            this.debouncedUpdateCharts()
          })
        } else {
          console.log('🚫 Chart update skipped - timeline is being adjusted')
        }
      }
    }
  },
  methods: {
    // Set up flexible back button based on how user arrived at Explorer
    setupBackButton() {
      try {
        // Check if user came from a specific route via URL parameters
        // Use Vue Router's query object instead of window.location.search
        const curveParam = this.$route.query.curve
        const fromParam = this.$route.query.from
        
        // Check if user came from browser history (referrer)
        const referrer = document.referrer
        const currentPath = this.$route.path
        
        console.log('🔍 Setting up back button:', {
          curveParam,
          fromParam,
          routeQuery: this.$route.query,
          referrer,
          currentPath,
          hasReferrer: !!referrer
        })
        
        // Determine the best back destination
        let backConfig = null
        
        // First, check query parameter (most reliable)
        if (fromParam) {
          if (fromParam === 'curve-results') {
            backConfig = {
              text: 'Return to Curve Results',
              route: '/curve-results'
            }
          } else if (fromParam === 'subject-dashboard') {
            backConfig = {
              text: 'Return to Dashboard',
              route: '/subject-dashboard'
            }
          } else if (fromParam === 'adherence-report') {
            backConfig = {
              text: 'Return to Adherence Report',
              route: '/adherence-report'
            }
          } else if (fromParam === 'histogram-results') {
            backConfig = {
              text: 'Return to Histogram Results',
              route: '/histogram-results'
            }
          }
        }
        
        // Fallback to referrer check if no query parameter
        if (!backConfig && referrer) {
          // Parse referrer to determine source
          const referrerUrl = new URL(referrer)
          const referrerPath = referrerUrl.pathname
          
          if (referrerPath.includes('/subject-dashboard')) {
            backConfig = {
              text: 'Return to Dashboard',
              route: '/subject-dashboard'
            }
          } else if (referrerPath.includes('/curve-results')) {
            backConfig = {
              text: 'Return to Curve Results',
              route: '/curve-results'
            }
          } else if (referrerPath.includes('/adherence-report')) {
            backConfig = {
              text: 'Return to Adherence Report',
              route: '/adherence-report'
            }
          } else if (referrerPath.includes('/histogram-results')) {
            backConfig = {
              text: 'Return to Histogram Results',
              route: '/histogram-results'
            }
          }
        }
        
        // Fallback: if no specific source, default to dashboard
        if (!backConfig) {
          backConfig = {
            text: 'Return to Dashboard',
            route: '/subject-dashboard'
          }
        }
        
        // Set the back button
        this.setBackButton(backConfig)
        
        console.log('✅ Back button configured:', backConfig)
        
      } catch (error) {
        console.error('❌ Error setting up back button:', error)
        // Fallback to dashboard
        this.setBackButton({
          text: 'Return to Dashboard',
          route: '/subject-dashboard'
        })
      }
    },
    updateDaySelectionFromTimeline() {
      try {
        if (!this.dayData || this.dayData.length === 0 || !this.timelineStart || !this.timelineEnd) return
        const tlStart = new Date(this.timelineStart)
        const tlEnd = new Date(this.timelineEnd)
        if (isNaN(tlStart.getTime()) || isNaN(tlEnd.getTime())) return
        
        // Find a day whose start/end exactly match (within 1 minute tolerance)
        const toleranceMs = 60 * 1000
        let matchedIndex = -1
        for (let i = 0; i < this.dayData.length; i++) {
          const d = this.dayData[i]
          const dayStart = new Date(d.begin_day || d.start_time || d.date)
          const dayEnd = new Date(d.end_day || d.end_time || d.date)
          if (isNaN(dayStart.getTime()) || isNaN(dayEnd.getTime())) continue
          const startDiff = Math.abs(tlStart.getTime() - dayStart.getTime())
          const endDiff = Math.abs(tlEnd.getTime() - dayEnd.getTime())
          if (startDiff <= toleranceMs && endDiff <= toleranceMs) {
            matchedIndex = i
            break
          }
        }
        if (matchedIndex !== -1 && this.selectedDayIndex !== matchedIndex.toString()) {
          console.log('🔄 Auto-updating day selection from timeline match -> Day', matchedIndex + 1)
          this.selectedDayIndex = matchedIndex.toString()
          // Emit same behavior as manual selection
          this.handleDaySelectionChange({ dayIndex: matchedIndex, day: this.dayData[matchedIndex] })
        }
      } catch (e) {
        console.warn('updateDaySelectionFromTimeline error:', e)
      }
    },
    // Toggle between Curve and Day view modes
    toggleViewMode() {
      console.log('🔄 Toggle view mode from', this.viewMode, 'to', this.viewMode === 'curve' ? 'day' : 'curve')
      this.viewMode = this.viewMode === 'curve' ? 'day' : 'curve'
      
      // Reset selections when switching modes
      if (this.viewMode === 'day') {
        // Switching to day mode - reset curve selection
        // Store current curve selection before resetting
        this.previousCurveIndex = this.selectedCurveIndex
        this.selectedCurveIndex = ''
        
        // Auto-select first day if no previous day was selected
        if (this.dayData && this.dayData.length > 0) {
          if (this.previousDayIndex !== '') {
            // Restore the previous day selection
            console.log('🔄 Switching to day mode, restoring day:', this.previousDayIndex)
            this.selectedDayIndex = this.previousDayIndex
            // Trigger day selection change to update timeline
            this.handleDaySelectionChange({
              dayIndex: parseInt(this.previousDayIndex),
              day: this.dayData[parseInt(this.previousDayIndex)]
            })
          } else {
            // Auto-select first day
            console.log('🔄 Switching to day mode, auto-selecting first day')
            this.selectedDayIndex = '0'
            // Trigger day selection change to update timeline
            this.handleDaySelectionChange({
              dayIndex: 0,
              day: this.dayData[0]
            })
          }
        }
      } else {
        // Switching to curve mode - store current day selection and reset it
        this.previousDayIndex = this.selectedDayIndex
        console.log('🔄 Storing previous day selection:', this.previousDayIndex)
        this.selectedDayIndex = ''

        // Auto-select first curve if no previous curve was selected
        if (this.processingResults?.curve_features?.length > 0) {
          if (this.previousCurveIndex !== '') {
            // Restore the previous curve selection
            console.log('🔄 Switching to curve mode, restoring curve:', this.previousCurveIndex)
            this.selectedCurveIndex = this.previousCurveIndex
            // Update timeline to focus on the restored curve
            this.onCurveSelectionChange({ curveIndex: parseInt(this.previousCurveIndex) })
          } else {
            // Auto-select first curve
            console.log('🔄 Switching to curve mode, auto-selecting first curve')
            this.selectedCurveIndex = '0'
            // Update timeline to focus on the first curve
            this.onCurveSelectionChange({ curveIndex: 0 })
          }
        }
      }
    },
    
    // Chart toggle methods
    toggleSignalChartExpansion() {
      this.isSignalChartExpanded = !this.isSignalChartExpanded
      
      // With v-if, the chart will be completely recreated when expanded
      // No need for complex resize/update logic since it's a fresh chart instance
      if (this.isSignalChartExpanded) {
        console.log('🔄 Signal chart expanded - will be recreated with v-if')
      } else {
        console.log('🔄 Signal chart collapsed - will be destroyed with v-if')
      }
    },
    
    toggleNonWearDetectionChartExpansion() {
      this.isNonWearDetectionChartExpanded = !this.isNonWearDetectionChartExpanded
      
      // With v-if, the chart will be completely recreated when expanded
      // No need for complex resize/update logic since it's a fresh chart instance
      if (this.isNonWearDetectionChartExpanded) {
        console.log('🔄 Non-wear detection chart expanded - will be recreated with v-if')
      } else {
        console.log('🔄 Non-wear detection chart collapsed - will be destroyed with v-if')
      }
    },
    
    // Y-axis resize handlers for Signal Processing Chart
    startResizeSignalChart(event) {
      event.preventDefault()
      
      // Clean up any existing resize handlers first
      this.stopResizing()
      
      this.isResizingSignalChart = true
      this.resizeStartY = event.clientY
      this.resizeStartHeight = this.signalChartHeight
      const defaultHeight = 600
      const minHeight = defaultHeight // 1x default size
      const maxHeight = defaultHeight * 2 // 2x default size
      
      this.resizeMouseMoveHandler = (e) => {
        if (!this.isResizingSignalChart || this._isUnmounting) return
        
        const deltaY = e.clientY - this.resizeStartY
        const newHeight = Math.max(minHeight, Math.min(maxHeight, this.resizeStartHeight + deltaY))
        this.signalChartHeight = newHeight
      }
      
      this.resizeMouseUpHandler = () => {
        this.stopResizing()
      }
      
      document.addEventListener('mousemove', this.resizeMouseMoveHandler)
      document.addEventListener('mouseup', this.resizeMouseUpHandler)
      document.body.style.cursor = 'ns-resize'
      document.body.style.userSelect = 'none'
    },
    
    // Y-axis resize handlers for Non-Wear Detection Chart
    startResizeNonWearChart(event) {
      event.preventDefault()
      
      // Clean up any existing resize handlers first
      this.stopResizing()
      
      this.isResizingNonWearChart = true
      this.resizeStartY = event.clientY
      this.resizeStartHeight = this.nonWearChartHeight
      const defaultHeight = 420
      const minHeight = defaultHeight // 1x default size
      const maxHeight = defaultHeight * 2 // 2x default size
      
      this.resizeMouseMoveHandler = (e) => {
        if (!this.isResizingNonWearChart || this._isUnmounting) return
        
        const deltaY = e.clientY - this.resizeStartY
        const newHeight = Math.max(minHeight, Math.min(maxHeight, this.resizeStartHeight + deltaY))
        this.nonWearChartHeight = newHeight
      }
      
      this.resizeMouseUpHandler = () => {
        this.stopResizing()
      }
      
      document.addEventListener('mousemove', this.resizeMouseMoveHandler)
      document.addEventListener('mouseup', this.resizeMouseUpHandler)
      document.body.style.cursor = 'ns-resize'
      document.body.style.userSelect = 'none'
    },
    
    // Stop resizing and clean up event listeners
    stopResizing() {
      if (this.resizeMouseMoveHandler) {
        document.removeEventListener('mousemove', this.resizeMouseMoveHandler)
        this.resizeMouseMoveHandler = null
      }
      if (this.resizeMouseUpHandler) {
        document.removeEventListener('mouseup', this.resizeMouseUpHandler)
        this.resizeMouseUpHandler = null
      }
      this.isResizingSignalChart = false
      this.isResizingNonWearChart = false
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    },
    
    
    formatFlagLabel(key) {
      try {
        // Remove leading FLAG_
        let k = key.replace(/^FLAG_/, '')
        // Replace underscores with spaces and title-case words
        k = k.replace(/_/g, ' ')
        k = k.replace(/\b\w/g, c => c.toUpperCase())
        
        // Remove redundant "Curve" word for incomplete curve flags
        // "Incomplete Curve Start Curve" -> "Incomplete Start Curve"
        // "Incomplete Curve End Curve" -> "Incomplete End Curve"
        k = k.replace(/Incomplete Curve (Start|End) Curve/g, 'Incomplete $1 Curve')
        
        return k
      } catch (_) {
        return key
      }
    },
    
    // NEW: Chart update methods using explicit calls instead of watchers
    
    // Note: We no longer manually update charts - they update automatically via computed properties
    // When data changes (currentTimeRangeData, selectedCurveIndex, etc.), the chart components
    // automatically re-render with the new data via their computed properties.
    
    // Update charts when timeline changes
    async onTimelineChange(timelineData) {
      // Guard: Don't process if component is unmounting
      if (this._isUnmounting) {
        return
      }
      
      try {
        // Validate timeline data before proceeding
        if (!timelineData || !timelineData.startTime || !timelineData.endTime) {
          console.warn('Invalid timeline data received:', timelineData)
          return
        }
        
        // Fetch new data for the timeline
        const newData = await this.fetchDataForTimeRange(
          timelineData.startTime, 
          timelineData.endTime
        )
        
        if (newData && newData.length > 0) {
          // CRITICAL: Update the data that charts depend on
          // This will trigger the chart components' computed properties to recalculate
          // Use Vue.set or direct assignment with defensive checks
          try {
            this.currentTimeRangeData = newData
          } catch (reactiveError) {
            console.error('Error updating reactive data:', reactiveError)
            return
          }
          
          // Force a reactivity update to ensure charts see the change
          await this.$nextTick()
          
          // Add error handling for chart updates
          try {
            // Force redraw: destroy and recreate charts so new range/data render immediately
            await this.manuallyUpdateCharts()
            
            // Force update both charts (even if hidden) to ensure they have the latest data
            this.forceUpdateBothCharts()
            
            // Update x-axis limits to align with the new timeline bounds
            this.updateChartXAxisLimits()
          } catch (chartError) {
            console.warn('Chart update error during timeline change:', chartError)
            // Don't rethrow - let the timeline update complete even if charts fail
          }
        } else {
          console.warn('No data fetched for timeline change')
        }
        
      } catch (error) {
        console.error('❌ Error handling timeline change:', error)
      }
    },
    
    // Checkbox change handler removed
    
    // Update charts when curve selection changes
    onCurveSelectionChange(curveData) {
      try {
        // CRITICAL: Don't manually update charts - let them update automatically
        // The chart components have computed properties that automatically process new data
        // When selectedCurveIndex changes, the charts will automatically re-render
      } catch (error) {
        console.error('Error handling curve selection change:', error)
      }
    },
    
    
    // Data processing by checkbox no longer needed; chart chooses columns directly
    
    // Helper method to create curve highlight options
    createCurveHighlightOptions(selectedCurveIndex) {
      // Create options that highlight the selected curve
      return {
        plugins: {
          // Add curve highlighting logic here
        }
      }
    },
    
    // PHASE-BASED INITIALIZATION METHODS
    
    // Phase 1: Data Validation - Ensure all required data is present
    async validateRequiredData() {
      try {
        // CRITICAL: Always reload from sessionStorage first to get the latest data
        // This ensures manual quality label changes persist when navigating back to Explorer
        const storedResults = sessionStorage.getItem('processingResults')
        if (storedResults) {
          const results = JSON.parse(storedResults)
          // Update global state with the latest data from sessionStorage
          this.updateGlobalState({ processingResults: results })
        }
        
        // Load data from global state (now refreshed with latest sessionStorage data)
        if (this.globalState.configuredData) {
          this.configuredData = this.globalState.configuredData
        }
        
        if (this.globalState.processingResults) {
          try {
            // CRITICAL: Force Vue reactivity by creating a new object reference
            // This ensures TimelineComponent receives updated curve_features
            this.processingResults = { ...this.globalState.processingResults }
            
            // Populate day data after loading processing results
            this.populateDayData()
          } catch (parseError) {
            console.error('Error parsing stored results:', parseError)
          }
        } else {
          console.error('No processingResults found in global state')
        }
        
        // Validate required data
        const errors = []
        
        if (!this.processingResults) {
          errors.push('No processing results found')
        }
        
        // For prior jobs, s3_key is sufficient (session_id will be null)
        // For active sessions, session_id is required
        const hasSessionId = !!this.processingResults?.session_id
        const hasS3Key = !!this.processingResults?.s3_key
        
        if (!hasSessionId && !hasS3Key) {
          errors.push('No session ID or S3 key found')
          console.error('❌ Data validation failed:', errors)
        }
        
        // Note: Curve features are optional - data may not have any curves detected
        // if (!this.processingResults?.curve_features?.length) {
        //   errors.push('No curve features found')
        // }
        
        // Check for timeline data - it can be in start_time/end_time, data_metadata, OR in curve features
        // If no curves exist, we'll fetch timeline data from the full dataset
        const hasStartTime = this.processingResults?.start_time || 
                           this.processingResults?.data_metadata?.start_time ||
                           (this.processingResults?.curve_features?.length > 0 && 
                            this.processingResults.curve_features[0]?.begin_CURVE)
        
        const hasEndTime = this.processingResults?.end_time || 
                          this.processingResults?.data_metadata?.end_time ||
                          (this.processingResults?.curve_features?.length > 0 && 
                           this.processingResults.curve_features[this.processingResults.curve_features.length - 1]?.end_CURVE)
        
        // Timeline data is not required at this stage - we'll fetch it from the full dataset if missing
        // if (!hasStartTime || !hasEndTime) {
        //   errors.push('Missing timeline data in metadata or curve features')
        // }
        
        if (errors.length > 0) {
          return { isValid: false, errors }
        }
        
        // CRITICAL: Fetch the full dataset timeline range BEFORE timeline initialization
        // This ensures we have the complete dataset range available for timeline setup
        // Try to fetch timeline range if we have either session_id OR s3_key
        if (this.processingResults?.session_id || this.processingResults?.s3_key) {
          const range = await this.fetchDatasetTimelineRange()
          this.datasetTimelineRange = normalizeTimelineRange(range)
          
          if (!this.datasetTimelineRange) {
            console.warn('⚠️ Failed to fetch full dataset timeline range, will use fallback data')
            // If fetch failed, try to extract from data_metadata as fallback
            if (this.processingResults?.data_metadata?.start_time && this.processingResults?.data_metadata?.end_time) {
              this.datasetTimelineRange = {
                earliest: this.processingResults.data_metadata.start_time,
                latest: this.processingResults.data_metadata.end_time
              }
            }
          }
        } else {
          console.warn('⚠️ No session ID or s3_key found, cannot fetch timeline range')
          // Try to extract from data_metadata as fallback
          if (this.processingResults?.data_metadata?.start_time && this.processingResults?.data_metadata?.end_time) {
            this.datasetTimelineRange = {
              earliest: this.processingResults.data_metadata.start_time,
              latest: this.processingResults.data_metadata.end_time
            }
          }
        }
        
        return { isValid: true, errors: [] }
      } catch (error) {
        console.error('Error validating data:', error)
        return { isValid: false, errors: [error.message] }
      }
    },
    
    // Phase 2: Timeline Initialization - Set up timeline with validated data
    async initializeTimeline() {
      console.log('📋 Initializing timeline...')
      
      try {
        // Get timeline data from metadata or curve features
        let startTime, endTime, startTimeString, endTimeString
        
        if (this.processingResults.start_time && this.processingResults.end_time) {
          // Use direct timeline data if available
          startTimeString = this.processingResults.start_time
          endTimeString = this.processingResults.end_time
          startTime = new Date(startTimeString)
          endTime = new Date(endTimeString)
          console.log('🔍 Using direct timeline data (root):', { startTimeString, endTimeString, startTime, endTime })
        } else if (this.processingResults.data_metadata?.start_time && this.processingResults.data_metadata?.end_time) {
          // Use timeline data from data_metadata
          startTimeString = this.processingResults.data_metadata.start_time
          endTimeString = this.processingResults.data_metadata.end_time
          startTime = new Date(startTimeString)
          endTime = new Date(endTimeString)
          console.log('🔍 Using timeline data from data_metadata:', { startTimeString, endTimeString, startTime, endTime })
        } else if (this.processingResults.curve_features?.length > 0) {
          // When curves are present: start 1 hour before first curve, end 1 hour after curve end
          // But cap the total duration at 24 hours from the start
          const firstCurve = this.processingResults.curve_features[0]
          const curveStartTime = new Date(firstCurve.begin_CURVE)
          const curveEndTime = new Date(firstCurve.end_CURVE)
          
          // Start 1 hour before curve start
          startTime = new Date(curveStartTime.getTime() - (60 * 60 * 1000))
          
          // End 1 hour after curve end
          const desiredEndTime = new Date(curveEndTime.getTime() + (60 * 60 * 1000))
          
          // Calculate desired duration
          const desiredDurationHours = (desiredEndTime.getTime() - startTime.getTime()) / (1000 * 60 * 60)
          
          // Cap at 24 hours if it exceeds the limit
          if (desiredDurationHours > this.maxTimeRangeHours) {
            endTime = new Date(startTime.getTime() + (this.maxTimeRangeHours * 60 * 60 * 1000))
            console.log(`🎯 Curve timeline duration ${desiredDurationHours.toFixed(1)} hours exceeds ${this.maxTimeRangeHours}-hour limit, capping at ${this.maxTimeRangeHours} hours`)
          } else {
            endTime = desiredEndTime
          }
          
          // Convert to timezone-naive ISO strings (YYYY-MM-DDTHH:MM:SS)
          const formatNaiveISO = (date) => {
            const year = date.getFullYear()
            const month = String(date.getMonth() + 1).padStart(2, '0')
            const day = String(date.getDate()).padStart(2, '0')
            const hours = String(date.getHours()).padStart(2, '0')
            const minutes = String(date.getMinutes()).padStart(2, '0')
            const seconds = String(date.getSeconds()).padStart(2, '0')
            return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`
          }
          
          startTimeString = formatNaiveISO(startTime)
          endTimeString = formatNaiveISO(endTime)
          
          console.log('🔍 Using curve-focused timeline (curve start - 1 hour to curve end + 1 hour, capped at 24h):', { 
            curveStart: firstCurve.begin_CURVE,
            curveEnd: firstCurve.end_CURVE,
            timelineStart: startTimeString,
            timelineEnd: endTimeString,
            desiredDurationHours: desiredDurationHours.toFixed(1),
            actualDurationHours: ((endTime.getTime() - startTime.getTime()) / (1000 * 60 * 60)).toFixed(1),
            wasCapped: desiredDurationHours > this.maxTimeRangeHours
          })
        } else {
          // Fallback: Use dataset timeline range when no other timeline data is available
          if (this.datasetTimelineRange && this.datasetTimelineRange.earliest && this.datasetTimelineRange.latest) {
            console.log('🔍 No timeline data from metadata or curves, using dataset range')
            startTimeString = this.datasetTimelineRange.earliest
            endTimeString = this.datasetTimelineRange.latest
            startTime = new Date(startTimeString)
            endTime = new Date(endTimeString)
          } else {
            // Final fallback: Use a default 24-hour range
            console.log('🔍 No timeline data available anywhere, using default 24-hour range')
            const now = new Date()
            startTime = new Date(now.getTime() - (12 * 60 * 60 * 1000)) // 12 hours ago
            endTime = new Date(now.getTime() + (12 * 60 * 60 * 1000)) // 12 hours from now
            startTimeString = startTime.toISOString()
            endTimeString = endTime.toISOString()
            
            console.log('🔍 Using default timeline range:', { 
              startTimeString, 
              endTimeString,
              startTime, 
              endTime
            })
          }
        }
        
        console.log('🔍 Timeline initialization debug:', {
          hasProcessingResults: !!this.processingResults,
          startTimeRaw: startTime,
          endTimeRaw: endTime,
          startTimeType: typeof startTime,
          endTimeType: typeof endTime
        })
        
        // Validate timeline order
        if (startTime.getTime() > endTime.getTime()) {
          console.error('❌ Invalid timeline order detected, swapping...')
          [startTime, endTime] = [endTime, startTime]
        }
        
        // Check 24-hour limit
        const durationHours = (endTime.getTime() - startTime.getTime()) / (1000 * 60 * 60)
        if (durationHours > this.maxTimeRangeHours) {
          console.log(`🎯 Duration ${durationHours.toFixed(1)} hours exceeds ${this.maxTimeRangeHours}-hour limit, limiting...`)
          const limitedEndTime = new Date(startTime.getTime() + (this.maxTimeRangeHours * 60 * 60 * 1000))
          endTime = limitedEndTime
          // Update endTimeString to match the limited end time
          const formatNaiveISO = (date) => {
            const year = date.getFullYear()
            const month = String(date.getMonth() + 1).padStart(2, '0')
            const day = String(date.getDate()).padStart(2, '0')
            const hours = String(date.getHours()).padStart(2, '0')
            const minutes = String(date.getMinutes()).padStart(2, '0')
            const seconds = String(date.getSeconds()).padStart(2, '0')
            return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`
          }
          endTimeString = formatNaiveISO(endTime)
        }
        
        // Set timeline to full dataset range initially (this will be updated to curve-focused range below)
        // Use raw timezone-naive strings directly (no UTC conversion)
        this.timelineStart = startTimeString
        this.timelineEnd = endTimeString
        
        console.log('🔍 Timeline state storage debug:', {
          originalStartTime: startTime,
          originalEndTime: endTime,
          storedTimelineStart: this.timelineStart,
          storedTimelineEnd: this.timelineEnd,
          usedRawStrings: true
        })
        
        // Initialize timeline range and percentages
        this.timelineRange = {
          start: startTime,
          mid: new Date(startTime.getTime() + (endTime.getTime() - startTime.getTime()) / 2),
          end: endTime
        }
        

        
        // Set initial curve as selected if available and immediately focus timeline on it
        if (this.processingResults.curve_features?.length > 0) {
          // Check for curve parameter in URL first, then use prop, then default to 0
          const urlParams = new URLSearchParams(window.location.search)
          const urlCurveParam = urlParams.get('curve')
          
          let initialIndex = 0
          if (urlCurveParam !== null) {
            initialIndex = Number(urlCurveParam)
            console.log(`🎯 Using curve from URL parameter: ${urlCurveParam}`)
          } else {
            // Always use the initialCurveIndex prop (defaults to 0 for Curve 1)
            initialIndex = Number(this.initialCurveIndex)
            console.log(`🎯 Using curve from prop: ${this.initialCurveIndex}`)
          }
          
          // Ensure the index is within bounds
          const validIndex = Math.max(0, Math.min(initialIndex, this.processingResults.curve_features.length - 1))
          this.selectedCurveIndex = validIndex.toString()
          
          console.log(`🎯 Initial curve selection: url=${urlCurveParam}, prop=${this.initialCurveIndex}, calculated=${initialIndex}, valid=${validIndex}, total curves=${this.processingResults.curve_features.length}`)
          
                  // CRITICAL: Set default focus to curve range within full dataset boundaries
        console.log('🎯 Setting default focus to curve range within full dataset boundaries')
        this._settingInitialTimeline = true
        
        // Set the timeline focus to curve range ±1 hour but keep full dataset as boundaries
        if (this.processingResults.curve_features?.length > 0) {
          const firstCurve = this.processingResults.curve_features[0]
          const curveStart = firstCurve.begin_CURVE
          const curveEnd = firstCurve.end_CURVE
          
          // Add ±1 hour padding to curve range (working with timezone-naive strings)
          // Parse the timezone-naive strings and add/subtract 1 hour
          const curveStartDate = new Date(curveStart + 'Z') // Add Z to treat as UTC for calculation
          const curveEndDate = new Date(curveEnd + 'Z')
          
          const paddedStartDate = new Date(curveStartDate.getTime() - (1 * 60 * 60 * 1000)) // -1 hour
          const paddedEndDate = new Date(curveEndDate.getTime() + (1 * 60 * 60 * 1000))   // +1 hour
          
          // Convert back to timezone-naive strings (remove Z and milliseconds)
          const paddedStart = paddedStartDate.toISOString().replace('Z', '').replace(/\.\d{3}/, '')
          const paddedEnd = paddedEndDate.toISOString().replace('Z', '').replace(/\.\d{3}/, '')
          
          // Set timeline focus to curve range ±1 hour (this will be the default view)
          this.timelineStart = paddedStart
          this.timelineEnd = paddedEnd
          
          console.log('🎯 Timeline focus set to curve range ±1 hour:', {
            curveStart,
            curveEnd,
            paddedStart,
            paddedEnd,
            fullDatasetStart: this.datasetTimelineRange?.earliest || 'N/A (using curve data)',
            fullDatasetEnd: this.datasetTimelineRange?.latest || 'N/A (using curve data)'
          })
        }
        }
        
        console.log('✅ Timeline initialized:', {
          start: this.timelineStart,
          end: this.timelineEnd,
          durationHours: durationHours > this.maxTimeRangeHours ? this.maxTimeRangeHours : durationHours
        })
        
        return { success: true }
      } catch (error) {
        console.error('Error initializing timeline:', error)
        return { success: false, error: error.message }
      }
    },
    
    // Phase 3: Data Fetching - Get the actual data for the timeline range
    async fetchInitialData() {
      try {
        // Check server connection (non-blocking)
        this.checkServerStatus().catch(err => {
          console.warn('Server status check failed (non-blocking):', err.message)
        })
        
        // Dataset timeline range already fetched in PHASE 1
        // No need to fetch again here
        
        // Check if processed_dataset has data (even if empty array)
        if (this.processingResults?.processed_dataset !== undefined) {
          // If it's an empty array (length === 0), we still need to fetch from server
          if (this.processingResults.processed_dataset.length > 0) {
            this.currentTimeRangeData = this.processingResults.processed_dataset
            return { success: true }
          }
        }
        
        // No stored processed dataset OR it's empty - fetch from server
        const data = await this.fetchDataForTimeRange(this.timelineStart, this.timelineEnd)
        if (data && data.length > 0) {
          this.currentTimeRangeData = data
          return { success: true }
        } else {
          console.error('Failed to fetch data from server - data is null or empty')
          return { success: false, error: 'Failed to fetch data' }
        }
      } catch (error) {
        console.error('Error fetching initial data:', error)
        return { success: false, error: error.message }
      }
    },
    
    // Phase 4: Chart Creation - Create charts only after timeline and data are ready
    async createInitialCharts() {
      try {
        // Set flags to prevent interference
        this._settingInitialTimeline = true
        this.isUpdatingCharts = true
        this.chartUpdateInProgress = true
        
        // Clear any existing chart queues to start fresh
        if (this.$refs.signalChart && this.$refs.signalChart.clearChartQueue) {
          this.$refs.signalChart.clearChartQueue()
        }
        if (this.$refs.deviceChart && this.$refs.deviceChart.clearChartQueue) {
          this.$refs.deviceChart.clearChartQueue()
        }
        
        // Wait for components to be ready
        await this.$nextTick()
        
        // Add a small delay to ensure DOM is fully ready
        await new Promise(resolve => setTimeout(resolve, 100))
        
        // CRITICAL: Initialize the actual chart components with data
        // Initialize signal chart
        if (this.$refs.signalChart && this.$refs.signalChart.$refs.baseChart) {
          await this.$refs.signalChart.$refs.baseChart.initializeChart()
        } else {
          console.warn('⚠️ Signal chart or baseChart ref not available')
        }
        
        // Initialize device chart
        if (this.$refs.deviceChart && this.$refs.deviceChart.$refs.baseChart) {
          await this.$refs.deviceChart.$refs.baseChart.initializeChart()
        } else {
          console.warn('⚠️ Device chart or baseChart ref not available')
        }
        
        // Update chart x-axis limits
        this.updateChartXAxisLimits()
        
        // Update timeline display
        this.updateTimelineDisplay()
        
        return { success: true }
      } catch (error) {
        console.error('Error creating charts:', error)
        return { success: false, error: error.message }
      } finally {
        // Reset flags
        this._settingInitialTimeline = false
        this.isUpdatingCharts = false
        this.chartUpdateInProgress = false
      }
    },
    
    // Phase 5: Page Ready - Enable interactions only after everything is complete
    async setPageReady() {
      try {
        // Mark charts as complete
        this.chartCreationComplete = true
        
        // Mark component as fully initialized
        this._componentInitialized = true
        
        // Force reset all transitional state flags to ensure timeline is unfrozen
        this.forceResetTransitionalState()
        
        // Add a safety timeout to ensure timeline is unfrozen even if something goes wrong
        setTimeout(() => {
          if (this.isInTransitionalState) {
            this.forceResetTransitionalState()
          }
        }, 2000)
        
        // Charts are already configured with autoUpdate: true by default
        // No need to manually enable auto-update
        
        // Set up event listeners and timers
        this.setupEventListeners()
        this.setupTimers()
        this.setupChartErrorHandling()
      } catch (error) {
        console.error('Error setting page ready:', error)
      }
    },

    
    // Helper method to set up event listeners
    setupEventListeners() {
      // Add window event listeners for better drag handling
      window.addEventListener('blur', this.handleWindowBlur)
      window.addEventListener('focus', this.handleWindowFocus)
      window.addEventListener('keydown', this.handleKeyDown)
      window.addEventListener('visibilitychange', this.handleVisibilityChange)
      window.addEventListener('beforeunload', this.handleBeforeUnload)
      
      // Add click outside handler for dropdown
      document.addEventListener('click', this.handleClickOutside)
    },
    
    // Set up tab tracking in sessionStorage
    setupTabTracking() {
      const tabId = 'dataExplorer_' + Date.now()
      const trackingData = {
        isOpen: true,
        tabId: tabId,
        curveIndex: this.initialCurveIndex,
        timestamp: Date.now()
      }
      
      sessionStorage.setItem('dataExplorerOpen', JSON.stringify(trackingData))
      this._tabId = tabId
      
      console.log('📌 Explorer tab tracking set up:', trackingData)
    },
    
    // Set up inter-tab communication via postMessage
    setupInterTabCommunication() {
      // Listen for messages from other tabs (like CurveResults)
      window.addEventListener('message', (event) => {
        // Verify origin for security
        if (event.origin !== window.location.origin) {
          return
        }
        
        if (event.data.type === 'UPDATE_CURVE') {
          console.log('📨 Received curve update message:', event.data)
          this.handleCurveUpdateFromExternal(event.data.curveIndex)
        }
      })
      
      console.log('📡 Inter-tab communication set up')
    },
    
    // Handle curve update from external source (like CurveResults)
    handleCurveUpdateFromExternal(curveIndex) {
      try {
        const validIndex = Math.max(0, Math.min(Number(curveIndex), (this.processingResults?.curve_features?.length || 1) - 1))
        
        console.log(`🔄 Updating curve from external source: ${curveIndex} -> ${validIndex}`)
        
        // Update the selected curve
        this.selectedCurveIndex = validIndex.toString()
        
        // Update timeline to show the new curve
        this.setTimelineFromSpecificCurve(validIndex)
        
        // Update tab tracking
        const trackingData = JSON.parse(sessionStorage.getItem('dataExplorerOpen') || '{}')
        trackingData.curveIndex = validIndex
        trackingData.timestamp = Date.now()
        sessionStorage.setItem('dataExplorerOpen', JSON.stringify(trackingData))
        
        console.log('✅ Curve updated from external source')
      } catch (error) {
        console.error('❌ Error handling curve update from external source:', error)
      }
    },
    
    // Clean up tab tracking when component unmounts
    cleanupTabTracking() {
      try {
        const trackingData = JSON.parse(sessionStorage.getItem('dataExplorerOpen') || '{}')
        
        // Only remove if this is our tab
        if (trackingData.tabId === this._tabId) {
          sessionStorage.removeItem('dataExplorerOpen')
          console.log('🧹 Explorer tab tracking cleaned up')
        }
      } catch (error) {
        console.warn('⚠️ Error cleaning up tab tracking:', error)
      }
    },
    
    // Helper method to set up timers
    setupTimers() {
      // Set up periodic freeze state consistency check - reduced frequency to minimize unnecessary work
      this.freezeStateCheckInterval = setInterval(this.checkFreezeStateConsistency, 5000) // Every 5 seconds instead of 1
      
      // Set up safety timer for stuck chart updates
      this.chartUpdateSafetyTimer = setTimeout(() => {
        this.checkChartUpdateSafety()
      }, 5000) // 5 second safety timeout
      
      // Set up periodic chart health check - reduced frequency to minimize unnecessary work
      this.chartHealthCheckInterval = setInterval(this.checkChartHealth, 30000) // Every 30 seconds instead of 10
    },
    
    // Chart component event handlers
    

    
    // Non-wear detection chart event handlers
    onNonWearDetectionChartReady(chart) {
      this.deviceChart = chart
    },
    
    onNonWearDetectionChartUpdated(chart) {
      this.$emit('chart-updated', chart)
    },
    
    onNonWearDetectionChartError(error) {
      console.error('Non-wear detection chart error:', error)
      
      // Handle specific error types
      if (error.message && error.message.includes('Maximum call stack size exceeded')) {
        console.warn('🔄 Stack overflow detected in non-wear detection chart, attempting recovery...')
        this.recoverNonWearDetectionChart()
      }
      
      this.$emit('chart-error', error)
    },
    
    // Recovery method for non-wear detection chart
    async recoverNonWearDetectionChart() {
      try {
        console.log('🔄 Attempting to recover non-wear detection chart...')
        
        // Force stop any ongoing operations
        this.forceStopChartJS()
        
        // Wait a moment for cleanup
        await new Promise(resolve => setTimeout(resolve, 200))
        
        // Clear the chart queue
        if (this.$refs.deviceChart && this.$refs.deviceChart.clearChartQueue) {
          this.$refs.deviceChart.clearChartQueue()
        }
        
        // Force a component update
        this.$forceUpdate()
        
        console.log('✅ Adherence chart recovery initiated')
      } catch (error) {
        console.error('❌ Error during adherence chart recovery:', error)
      }
    },
    

    
    // Method to initialize timeline after data has been fetched
    initializeTimelineAfterDataFetch() {
      console.log('🎯 Initializing timeline after data fetch...')
      console.log('🎯 Current data available:', this.currentTimeRangeData?.length || 0)
      
      if (this.currentTimeRangeData?.length > 0) {
        // Get the actual full data range from fetched data
        const firstDataPoint = new Date(this.currentTimeRangeData[0].datetime)
        const lastDataPoint = new Date(this.currentTimeRangeData[this.currentTimeRangeData.length - 1].datetime)
        
        console.log('🎯 Actual data range:', {
          first: firstDataPoint.toISOString(),
          last: lastDataPoint.toISOString(),
          durationHours: ((lastDataPoint.getTime() - firstDataPoint.getTime()) / (1000 * 60 * 60)).toFixed(1)
        })
        
        // Update the timelineRange to reflect the actual full data range
        this.timelineRange = {
          start: firstDataPoint,
          mid: new Date(firstDataPoint.getTime() + (lastDataPoint.getTime() - firstDataPoint.getTime()) / 2),
          end: lastDataPoint
        }
        
        // Now try to set the timeline based on curves with the actual data available
        if (this.processingResults?.curve_features?.length > 0) {
          console.log('🎯 Attempting curve-based timeline initialization with actual data...')
          this._settingInitialTimeline = true
          const success = this.setTimelineFromCurves()
          
          if (success) {
            console.log('🎯 Successfully initialized curve-based timeline with actual data')
            // Update timeline percentages and display
            this.updateTimelinePercentages()
            this.updateTimelineDisplay()
            this.updateChartXAxisLimits()
          } else {
            console.warn('🎯 Failed to initialize curve-based timeline, using full data range')
            // Fallback to full data range (store as timezone-naive strings)
            this.timelineStart = firstDataPoint.toISOString().replace('Z', '').replace(/\.\d{3}/, '')
            this.timelineEnd = lastDataPoint.toISOString().replace('Z', '').replace(/\.\d{3}/, '')
    
          }
        } else {
          // No curves detected - determine timeline range based on data duration
          const dataDurationHours = (lastDataPoint.getTime() - firstDataPoint.getTime()) / (1000 * 60 * 60)
          console.log(`🎯 No curves detected, data duration: ${dataDurationHours.toFixed(1)} hours`)
          
          if (dataDurationHours > 24) {
            // Data > 24 hours: Show first 24 hours
            const endTime = new Date(firstDataPoint.getTime() + (24 * 60 * 60 * 1000))
            this.timelineStart = firstDataPoint.toISOString().replace('Z', '').replace(/\.\d{3}/, '')
            this.timelineEnd = endTime.toISOString().replace('Z', '').replace(/\.\d{3}/, '')
            console.log('🎯 Data > 24 hours, showing first 24 hours')
          } else {
            // Data ≤ 24 hours: Show full data range
            this.timelineStart = firstDataPoint.toISOString().replace('Z', '').replace(/\.\d{3}/, '')
            this.timelineEnd = lastDataPoint.toISOString().replace('Z', '').replace(/\.\d{3}/, '')
            console.log('🎯 Data ≤ 24 hours, showing full data range')
          }
        }
        
        // CRITICAL: Reset the flag to allow user interactions
        this._settingInitialTimeline = false
        console.log('🔓 Timeline initialization complete - user interactions now allowed')
        
        console.log('🎯 Final timeline state after data fetch:', {
          timelineStart: this.timelineStart,
          timelineEnd: this.timelineEnd,
          timelineRange: this.timelineRange,
          selectedRangePercent: this.selectedRangePercent
        })
      } else {
        console.warn('🎯 No data available for timeline initialization')
        // Also reset flag if no data
        this._settingInitialTimeline = false
      }
    },
    


    // Method to set timeline based on a specific curve index
    setTimelineFromSpecificCurve(curveIndex) {
      // Prevent execution during chart creation to avoid reactivity loops
      if (this.isUpdatingCharts || this.chartUpdateInProgress) {
        return false
      }
      
      // Allow execution during initial timeline setup for curve-based positioning
      if (this._settingInitialTimeline) {
        console.log('🔍 setTimelineFromSpecificCurve allowed during initial setup for curve positioning')
      }
      
              if (this.processingResults?.curve_features?.length > curveIndex) {
          // Use the new curve mapping system
          const selectedCurve = this.curveTimelineMap[curveIndex]
          
          // Check if the curve has start and end timestamps
          if (selectedCurve?.startDate && selectedCurve?.endDate) {
            try {
              const curveStart = selectedCurve.startDate
              const curveEnd = selectedCurve.endDate
              
              // Validate that the dates are valid
              if (isNaN(curveStart.getTime()) || isNaN(curveEnd.getTime())) {
                console.warn('Invalid curve timestamps:', { start: selectedCurve.start, end: selectedCurve.end })
                return false
              }
            
            // Get the full dataset range (prefer server-provided timeline range)
            let fullDataStart, fullDataEnd
            if (this.fullDatasetEarliestTime && this.fullDatasetLatestTime) {
              fullDataStart = new Date(this.fullDatasetEarliestTime)
              fullDataEnd = new Date(this.fullDatasetLatestTime)
              // Log for clarity
              console.log(`🔍 Using fullDataset* for curve ${parseInt(curveIndex) + 1} range:`, {
                earliest: fullDataStart.toISOString(),
                latest: fullDataEnd.toISOString()
              })
            } else if (this.processingResults?.data_metadata?.start_time && this.processingResults?.data_metadata?.end_time) {
              fullDataStart = new Date(this.processingResults.data_metadata.start_time)
              fullDataEnd = new Date(this.processingResults.data_metadata.end_time)
              console.log(`🔍 Using data_metadata for curve ${parseInt(curveIndex) + 1} range:`, {
                start: fullDataStart.toISOString(),
                end: fullDataEnd.toISOString()
              })
            } else if (this.processingResults?.processed_dataset?.length > 0) {
              // Fallback: use processed dataset to determine range
              const firstDataPoint = new Date(this.processingResults.processed_dataset[0].datetime)
              const lastDataPoint = new Date(this.processingResults.processed_dataset[this.processingResults.processed_dataset.length - 1].datetime)
              fullDataStart = firstDataPoint
              fullDataEnd = lastDataPoint
              console.log(`🔍 Using processed_dataset for curve ${parseInt(curveIndex) + 1} range:`, {
                first: firstDataPoint.toISOString(),
                last: lastDataPoint.toISOString()
              })
            } else if (this.currentTimeRangeData?.length > 0) {
              // LAST resort: use currently fetched data to determine range
              const firstDataPoint = new Date(this.currentTimeRangeData[0].datetime)
              const lastDataPoint = new Date(this.currentTimeRangeData[this.currentTimeRangeData.length - 1].datetime)
              fullDataStart = firstDataPoint
              fullDataEnd = lastDataPoint
              console.log(`🔍 Using currentTimeRangeData for curve ${parseInt(curveIndex) + 1} range:`, {
                first: firstDataPoint.toISOString(),
                last: lastDataPoint.toISOString(),
                count: this.currentTimeRangeData.length
              })
            } else {
              console.warn('❌ No data range information available')
              return false
            }
            
            console.log(`🔍 Full data range for curve ${parseInt(curveIndex) + 1}:`, {
              start: fullDataStart.toISOString(),
              end: fullDataEnd.toISOString()
            })
            
                        // For curve selection: Always show selected curve ±1 hour
            // For timeline expansion: Use full data range (with 24-hour limit)
            if (this._settingInitialTimeline || this.selectedCurveIndex !== '') {
              // Initial load OR curve selection: Show selected curve ±1 hour
              let startTime = new Date(curveStart.getTime() - (1 * 60 * 60 * 1000))
              let endTime = new Date(curveEnd.getTime() + (1 * 60 * 60 * 1000))
              
              // CRITICAL: Ensure startTime is always before endTime
              if (startTime >= endTime) {
                console.error('❌ Invalid curve timeline calculation detected:', {
                  curveStart: curveStart.toISOString(),
                  curveEnd: curveEnd.toISOString(),
                  calculatedStart: startTime.toISOString(),
                  calculatedEnd: endTime.toISOString()
                })
                
                // Calculate the curve duration to determine appropriate buffer
                const curveDuration = curveEnd.getTime() - curveStart.getTime()
                const bufferMs = Math.max(curveDuration, 30 * 60 * 1000) // At least 30 minutes, or curve duration if longer
                
                // Fix by using the curve times directly with appropriate buffer
                startTime = new Date(curveStart.getTime() - bufferMs)
                endTime = new Date(curveEnd.getTime() + bufferMs)
                
                console.log('🔄 Fixed curve timeline calculation with dynamic buffer:', {
                  curveDuration: (curveDuration / (1000 * 60)).toFixed(1) + ' minutes',
                  bufferMs: (bufferMs / (1000 * 60)).toFixed(1) + ' minutes',
                  start: startTime.toISOString(),
                  end: endTime.toISOString()
                })
              }
              
              // Ensure we don't go beyond available data
              if (startTime < fullDataStart) startTime = new Date(fullDataStart.getTime())
              if (endTime > fullDataEnd) endTime = new Date(fullDataEnd.getTime())
              
              // Final validation: ensure start is before end
              if (startTime >= endTime) {
                console.error('❌ Timeline validation failed after bounds check:', {
                  start: startTime.toISOString(),
                  end: endTime.toISOString(),
                  fullDataStart: fullDataStart.toISOString(),
                  fullDataEnd: fullDataEnd.toISOString()
                })
                
                // If bounds checking caused invalid range, use a minimal valid range
                if (fullDataEnd.getTime() > fullDataStart.getTime()) {
                  // Use the full data range as fallback
                  startTime = new Date(fullDataStart.getTime())
                  endTime = new Date(fullDataEnd.getTime())
                  
                  console.log('🔄 Using full data range as fallback due to bounds check failure:', {
                    start: startTime.toISOString(),
                    end: endTime.toISOString()
                  })
                } else {
                  // If even the full data range is invalid, return false
                  console.error('❌ Full data range is also invalid, cannot set timeline')
                  return false
                }
              }
              
              this.timelineStart = startTime.toISOString().replace('Z', '').replace(/\.\d{3}/, '')
              this.timelineEnd = endTime.toISOString().replace('Z', '').replace(/\.\d{3}/, '')
              
              console.log(`🎯 Timeline set to curve ${parseInt(curveIndex) + 1} ±1 hour:`, {
                curveStart: curveStart.toISOString(),
                curveEnd: curveEnd.toISOString(),
                timelineStart: this.timelineStart,
                timelineEnd: this.timelineEnd,
                curveStartFormatted: this.curveTimelineMap[curveIndex]?.startFormatted,
                curveEndFormatted: this.curveTimelineMap[curveIndex]?.endFormatted,

              })
            } else {
              // Timeline expansion: Use full data range
              this.timelineStart = fullDataStart.toISOString().replace('Z', '').replace(/\.\d{3}/, '')
              this.timelineEnd = fullDataEnd.toISOString().replace('Z', '').replace(/\.\d{3}/, '')
              
              // Check if the full range exceeds 24 hours
              const fullRangeHours = (fullDataEnd.getTime() - fullDataStart.getTime()) / (1000 * 60 * 60)
              if (fullRangeHours > this.maxTimeRangeHours) {
                console.log(`🎯 Full data range ${fullRangeHours.toFixed(1)} hours exceeds ${this.maxTimeRangeHours}-hour limit`)
                
                // Adjust curve end to stay within 24 hours of start
                const maxCurveEnd = new Date(fullDataStart.getTime() + (this.maxTimeRangeHours * 60 * 60 * 1000))
                
                if (curveEnd > maxCurveEnd) {
                  console.log(`🎯 Adjusting curve ${parseInt(curveIndex) + 1} end from ${curveEnd.toISOString()} to ${maxCurveEnd.toISOString()} to stay within 24-hour limit`)
                  // Update the curve end in the processing results to reflect the adjustment
                  this.processingResults.curve_features[curveIndex].end_CURVE = maxCurveEnd.toISOString()
                  // Also update the timeline end to match the adjusted curve
                  this.timelineEnd = maxCurveEnd.toISOString().replace('Z', '').replace(/\.\d{3}/, '')
                }
                
                console.log(`🎯 Timeline limited to ${this.maxTimeRangeHours} hours: ${fullDataStart.toISOString()} to ${this.timelineEnd}`)
              }
            }
            
            // Update the timelineRange for display purposes - use full dataset range for the slider background
            // This ensures the timeline slider shows the full range while the selected range focuses on the curve
            this.timelineRange = {
              start: new Date(this.fullDatasetEarliestTime || fullDataStart),
              mid: new Date((new Date(this.fullDatasetEarliestTime || fullDataStart).getTime() + new Date(this.fullDatasetLatestTime || fullDataEnd).getTime()) / 2),
              end: new Date(this.fullDatasetLatestTime || fullDataEnd)
            }
            
            console.log('🎯 Timeline range updated for display:', {
              timelineRangeStart: this.timelineRange.start.toISOString(),
              timelineRangeEnd: this.timelineRange.end.toISOString(),
              timelineStart: this.timelineStart,
              timelineEnd: this.timelineEnd
            })
            
            // Calculate and update percentages for the full range
            this.updateTimelinePercentages()
            
                    // Update chart x-axis limits immediately for responsive timeline adjustment
        this.updateChartXAxisLimits()
        
        // Update the timeline display to reflect the new positioning
        this.updateTimelineDisplay()
        
        // Charts will automatically update via Vue reactivity when timeline changes
        console.log('📊 Timeline changed - charts will update automatically via Vue reactivity')
        
        // Update timeline percentages to reflect the new curve-focused view
        this.updateTimelinePercentages()
        
        // Charts will automatically update via Vue reactivity when timeline changes
        console.log('📊 Timeline changed - charts will update automatically via Vue reactivity')
        
        console.log(`🎯 Timeline set to full data range for curve ${parseInt(curveIndex) + 1}:`, {
          curveStart: curveStart.toISOString(),
          curveEnd: this.processingResults.curve_features[curveIndex].end_CURVE,
          timelineStart: this.timelineStart,
          timelineEnd: this.timelineEnd,
          fullRangeHours: ((new Date(this.timelineEnd).getTime() - fullDataStart.getTime()) / (1000 * 60 * 60)).toFixed(1)
        })
        
        return true
          } catch (error) {
            console.error('Error setting timeline from specific curve:', error)
            return false
          }
        } else {
          console.warn('Curve missing begin or end timestamps:', selectedCurve)
          return false
        }
      }
      return false
    },
    

    
    // Method to manually trigger curve-based timeline positioning
    async manualSetTimelineFromCurves() {
      if (this.isInTransitionalState) {
        console.log('Charts are updating, skipping manual curve positioning')
        return
      }
      
      const success = this.setTimelineFromCurves()
      if (success) {
        // Fetch data for the curve-based timeline
        this.debouncedFetchData(this.timelineStart, this.timelineEnd)
        // Show success feedback (you could add a toast notification here)
        console.log('Successfully repositioned timeline to first curve')
      } else {
        console.warn('Failed to position timeline based on curves')
      }
    },

    // Method to initialize curve-based timeline
    initializeCurveBasedTimeline() {
      if (this.processingResults?.curve_features?.length > 0) {
        // Use the selected curve index (which respects initialCurveIndex prop)
        const curveIndex = this.selectedCurveIndex !== '' ? parseInt(this.selectedCurveIndex) : 0
        return this.setTimelineFromSpecificCurve(curveIndex)
      }
      return false
    },

    // Method to set timeline based on curves (for timeline component)
    setTimelineFromCurves() {
      if (this.processingResults?.curve_features?.length > 0) {
        // Use the selected curve index (which respects initialCurveIndex prop)
        const curveIndex = this.selectedCurveIndex !== '' ? parseInt(this.selectedCurveIndex) : 0
        return this.setTimelineFromSpecificCurve(curveIndex)
      }
      return false
    },
    
    
    // Method to refresh charts with full data after timeline reset
    async refreshChartsWithFullData() {
      console.log('🔄 Refreshing charts with full data...')
      
      try {
        // Clear chart caches
        if (this.$refs.signalChart && this.$refs.signalChart.clearCaches) {
          this.$refs.signalChart.clearCaches()
        }
        if (this.$refs.deviceChart && this.$refs.deviceChart.clearCaches) {
          this.$refs.deviceChart.clearCaches()
        }
        
        // Clear error states to allow chart updates
        if (this.$refs.signalChart && this.$refs.signalChart.clearErrorState) {
          this.$refs.signalChart.clearErrorState()
        }
        if (this.$refs.deviceChart && this.$refs.deviceChart.clearErrorState) {
          this.$refs.deviceChart.clearErrorState()
        }
        
        // Force a data refresh by triggering the filteredData computed property
        await this.$nextTick()
        
        // Update chart x-axis limits to ensure proper display
        this.updateChartXAxisLimits()
        
        console.log('✅ Charts refreshed with full data')
      } catch (error) {
        console.error('Error refreshing charts with full data:', error)
      }
    },
    
    // Method to automatically detect and handle timeline zoom out to full range
    handleTimelineZoomOut() {
      console.log('🔍 Detecting timeline zoom out to full range...')
      
      // Check if the current timeline covers the full data range
      if (this.fullDatasetEarliestTime && this.fullDatasetLatestTime) {
        const fullStart = new Date(this.fullDatasetEarliestTime)
        const fullEnd = new Date(this.fullDatasetLatestTime)
        const currentStart = new Date(this.timelineStart)
        const currentEnd = new Date(this.timelineEnd)
        
        // Check if timeline is close to full range (within 1 minute tolerance)
        const startDiff = Math.abs(currentStart.getTime() - fullStart.getTime())
        const endDiff = Math.abs(currentEnd.getTime() - fullEnd.getTime())
        const tolerance = 60 * 1000 // 1 minute
        
        if (startDiff <= tolerance && endDiff <= tolerance) {
          console.log('🔍 Timeline is at full range, ensuring charts show complete data...')
          
          // Force refresh charts to ensure they display the full dataset
          this.refreshChartsWithFullData()
          
          // Also ensure the filteredData includes all data
          this.$forceUpdate()
          
          return true
        }
      }
      
      return false
    },
    
    // Method to clear error states from all charts
    clearChartErrorStates() {
      console.log('🔄 Clearing error states from all charts...')
      
      try {
        if (this.$refs.signalChart && this.$refs.signalChart.clearErrorState) {
          this.$refs.signalChart.clearErrorState()
        }
        if (this.$refs.deviceChart && this.$refs.deviceChart.clearErrorState) {
          this.$refs.deviceChart.clearErrorState()
        }
        
        console.log('✅ Chart error states cleared')
        return true
      } catch (error) {
        console.error('Error clearing chart error states:', error)
        return false
      }
    },
    
    
    
    // Method to select a specific curve and position timeline around it
    selectCurve() {
      console.log('🔄 selectCurve called')
      
      // Allow curve selection during initial setup (before charts are created)
      // Only apply freeze guard after initial setup is complete
      if (this.chartCreationComplete && this.isInTransitionalState) {
        console.log('🚫 Curve selection blocked - page not ready for interaction')
        return
      }
      
      // Prevent multiple rapid calls
      if (this._selectingCurve) {
        console.log('🔄 selectCurve blocked - already selecting')
        return
      }
      
      if (this.selectedCurveIndex === '') {
        console.log('🔄 selectCurve blocked - no selection')
        return
      }
      
      this._selectingCurve = true
      
      try {
        const success = this.setTimelineFromSpecificCurve(this.selectedCurveIndex)
        if (success) {
          console.log(`Successfully repositioned timeline to curve ${parseInt(this.selectedCurveIndex) + 1}`)
          
          // CRITICAL: Update chart x-axis limits to zoom in on the selected curve
          this.updateChartXAxisLimits()
          
          // Update curve start/end datasets to reflect selection
          this.updateCurveDatasets()

          // Fetch data for the newly selected timeline and refresh charts
          // Use the explicit handler which fetches and then destroy/recreate charts safely
          this.onTimelineChange({ startTime: this.timelineStart, endTime: this.timelineEnd })
          // Ensure x-axis matches the intended full curve ±1 hour view even if page is busy
          this.updateChartXAxisLimits(true)
          
          console.log(`🎯 Chart zoomed in on curve ${parseInt(this.selectedCurveIndex) + 1} ±1 hour`)
        } else {
          console.warn(`Failed to position timeline based on curve ${parseInt(this.selectedCurveIndex) + 1}`)
        }
      } finally {
        // Reset the flag after a delay to prevent rapid successive calls
        setTimeout(() => {
          this._selectingCurve = false
        }, 200)
      }
    },
    
    // Toggle curve dropdown visibility
    toggleCurveDropdown() {
      this.showCurveDropdown = !this.showCurveDropdown
    },
    
    // Select curve from dropdown menu
    selectCurveFromDropdown(curveIndex) {
      this.selectedCurveIndex = curveIndex.toString()
      this.showCurveDropdown = false
      this.selectCurve()
    },
    
    // Update curve start/end datasets when selection changes
    updateCurveDatasets() {
      console.log('🔄 updateCurveDatasets called')
      
      // Prevent infinite recursion
      if (this._updatingCurveDatasets) {
        console.log('🔄 updateCurveDatasets blocked - already updating')
        return
      }
      
      // Additional safety checks
      if (this.isInTransitionalState) {
        console.log('🔄 updateCurveDatasets blocked - chart in unstable state')
        return
      }
      
      this._updatingCurveDatasets = true
      
      try {
        console.log('🔄 Updating curve dataset properties for both charts')
        
        // Ensure charts are ready before attempting updates
        if (!this.chartCreationComplete) {
          console.warn('⚠️ Charts not yet created, skipping curve dataset update')
          return
        }
        
        // Update Signal Processing Chart (only if visible)
        if (this.isSignalChartExpanded) {
          const signalChart = this.$refs.signalChart?.getChart()
          if (signalChart) {
            this.updateChartCurveDatasets(signalChart, 'Signal Processing')
          } else {
            console.warn('⚠️ Signal chart not available for update')
          }
        }
        
        // Update Non-Wear Detection Chart (only if visible)
        if (this.isNonWearDetectionChartExpanded) {
          const deviceChart = this.$refs.deviceChart?.getChart()
          if (deviceChart) {
            this.updateChartCurveDatasets(deviceChart, 'Non-Wear Detection')
          } else {
            console.warn('⚠️ Device chart not available for update')
          }
        }
        
        console.log('✅ Both charts updated with new curve selection')
        
        // Also force update both charts to ensure they render the changes
        this.forceUpdateBothCharts()
        
      } catch (error) {
        console.warn('Error updating curve datasets:', error)
      } finally {
        this._updatingCurveDatasets = false
      }
    },
    
    // Force update both charts when timeline or data changes (only if visible)
    forceUpdateBothCharts() {
      try {
        // Update Signal Processing Chart (only if visible)
        if (this.isSignalChartExpanded) {
          const signalChart = this.$refs.signalChart?.getChart()
          if (signalChart) {
            try {
              signalChart.update('none')
            } catch (chartError) {
              console.warn('Error updating signal chart:', chartError)
              // Handle Chart.js plugin errors specifically
              if (chartError.message && chartError.message.includes('includes')) {
                console.warn('Chart.js plugin error detected in signal chart, attempting recovery')
                this.handleChartPluginError()
              }
            }
          }
        }
        
        // Update Non-Wear Detection Chart (only if visible)
        if (this.isNonWearDetectionChartExpanded) {
          const deviceChart = this.$refs.deviceChart?.getChart()
          if (deviceChart) {
            try {
              deviceChart.update('none')
            } catch (chartError) {
              console.warn('Error updating device chart:', chartError)
              // Handle Chart.js plugin errors specifically
              if (chartError.message && chartError.message.includes('includes')) {
                console.warn('Chart.js plugin error detected in device chart, attempting recovery')
                this.handleChartPluginError()
              }
            }
          }
        }
        
      } catch (error) {
        console.warn('Error force updating charts:', error)
        // Handle Chart.js plugin errors specifically
        if (error.message && error.message.includes('includes')) {
          console.warn('Chart.js plugin error detected in forceUpdateBothCharts, attempting recovery')
          this.handleChartPluginError()
        }
      }
    },
    
    // Helper method to update curve datasets for a specific chart
    updateChartCurveDatasets(chart, chartName) {
      if (!chart || !chart.data || !chart.data.datasets) {
        console.log(`🔄 ${chartName} chart not ready for curve dataset update`)
        return
      }
      
      console.log(`🔄 Updating curve datasets for ${chartName} chart`)
      
      // Find curve datasets in the chart and update them directly
      chart.data.datasets.forEach((chartDataset, index) => {
        if (chartDataset.label && (chartDataset.label.includes('Curve') && (chartDataset.label.includes('Start') || chartDataset.label.includes('End')))) {
          // Extract curve index from label (e.g., "Curve 1 Start" -> index 0)
          const match = chartDataset.label.match(/Curve (\d+) (Start|End)/)
          if (match) {
            const curveIndex = parseInt(match[1]) - 1
            const isSelected = this.selectedCurveIndex !== '' && parseInt(this.selectedCurveIndex) === curveIndex
            
            // Update only the visual properties, not the entire dataset
            if (chartDataset.label.includes('Start')) {
              // Curve start lines are always dark blue
              chartDataset.borderColor = 'rgba(0, 0, 139, 0.9)'
              chartDataset.backgroundColor = 'rgba(0, 0, 139, 0.1)'
            } else if (chartDataset.label.includes('End')) {
              // Curve end lines are always dark red
              chartDataset.borderColor = 'rgba(139, 0, 0, 0.9)'
              chartDataset.backgroundColor = 'rgba(139, 0, 0, 0.1)'
            }
            chartDataset.borderWidth = isSelected ? 3 : 2
            
            console.log(`🔄 Updated ${chartDataset.label} in ${chartName}: selected=${isSelected}`)
          }
        }
      })
      
      // Force chart update even if hidden
      try {
        if (chart && chart.update && typeof chart.update === 'function') {
          chart.update('none')
          console.log(`🔄 ${chartName} chart updated with new curve selection`)
        } else {
          console.warn(`⚠️ ${chartName} chart update method not available`)
        }
      } catch (error) {
        console.error(`Error updating ${chartName} chart:`, error)
      }
    },
    
    // Clean up any existing tooltips
    cleanupTooltips() {
      const tooltips = document.querySelectorAll('.curve-tooltip')
      tooltips.forEach(tooltip => {
        if (tooltip.parentNode) {
          tooltip.parentNode.removeChild(tooltip)
        }
      })
    },
    

    
    resetTimeline() {
      // Allow timeline reset during initial setup (before charts are created)
      // Only apply freeze guard after initial setup is complete
      if (this.chartCreationComplete && this.isInTransitionalState) {
        console.log('🚫 Timeline reset blocked - page not ready for interaction')
        return
      }
      
      // Check if we're currently showing full range or curve focus
      if (this.fullDatasetEarliestTime && this.fullDatasetLatestTime) {
        const fullStart = new Date(this.fullDatasetEarliestTime)
        const fullEnd = new Date(this.fullDatasetLatestTime)
        const timelineStart = new Date(this.timelineStart)
        const timelineEnd = new Date(this.timelineEnd)
        
        // If timeline covers full range (within 1 minute tolerance), focus back on curve
        const startDiff = Math.abs(timelineStart.getTime() - fullStart.getTime())
        const endDiff = Math.abs(timelineEnd.getTime() - fullEnd.getTime())
        const tolerance = 60 * 1000 // 1 minute
        
        if (startDiff <= tolerance && endDiff <= tolerance) {
          // Currently showing full range, focus back on selected curve
          console.log('🎯 Focusing timeline back on selected curve...')
          this._settingInitialTimeline = true
          this.setTimelineFromSpecificCurve(this.selectedCurveIndex || 0)
          return
        }
      }
      
      // Otherwise, expand to full range
      console.log('🎯 Expanding timeline to full data range...')
      
      // Set flag to indicate we're expanding timeline (not initial setup)
      this._settingInitialTimeline = false
      
      if (this.fullDatasetEarliestTime && this.fullDatasetLatestTime) {
        const first = new Date(this.fullDatasetEarliestTime)
        const last = new Date(this.fullDatasetLatestTime)
        
        // Check if the full range exceeds 24 hours and limit if necessary
        const fullRangeHours = (last.getTime() - first.getTime()) / (1000 * 60 * 60)
        let endTime = last
        
        if (fullRangeHours > this.maxTimeRangeHours) {
          endTime = new Date(first.getTime() + (this.maxTimeRangeHours * 60 * 60 * 1000))
          console.log(`🎯 Full range ${fullRangeHours.toFixed(1)} hours exceeds ${this.maxTimeRangeHours}-hour limit, limiting to 24 hours`)
        }
        
        this.timelineStart = first.toISOString().replace('Z', '').replace(/\.\d{3}/, '')
        this.timelineEnd = endTime.toISOString().replace('Z', '').replace(/\.\d{3}/, '')
        
        // Reset timeline percentages and range

        this.timelineRange = {
          start: first,
          mid: new Date(first.getTime() + (endTime.getTime() - first.getTime()) / 2),
          end: endTime
        }
        
        // Reset selected curve index
        this.selectedCurveIndex = ''
        
        // Update timeline percentages and display
        this.updateTimelinePercentages()
        this.updateTimelineDisplay()
        
        // Fetch data for the expanded range
        this.debouncedFetchData(this.timelineStart, this.timelineEnd)
        
        // Update chart x-axis limits immediately for responsive timeline adjustment
        this.updateChartXAxisLimits()
        
        // Update charts to reflect new timeline
        this.debouncedUpdateCharts()
        
        console.log('🎯 Timeline expanded to full data range:', {
          start: this.timelineStart,
          end: this.timelineEnd,
          rangeHours: ((endTime.getTime() - first.getTime()) / (1000 * 60 * 60)).toFixed(1)
        })
      }
    },
    

    
    updateTimelinePercentages() {
      // Prevent updates during chart creation or updates to avoid reactivity loops
      if (this.isUpdatingCharts || this.chartUpdateInProgress) {
        return
      }
      
      // Allow updates during initial timeline setup for curve-based positioning
      if (this._settingInitialTimeline) {
        console.log('🔍 updateTimelinePercentages allowed during initial setup for curve positioning')
      }
      
      if (!this.fullDatasetEarliestTime || !this.fullDatasetLatestTime) return
      // Ensure percentages object exists
      if (!this.selectedRangePercent || typeof this.selectedRangePercent !== 'object') {
        this.selectedRangePercent = { start: 0, end: 100 }
      }
      
      const first = new Date(this.fullDatasetEarliestTime)
      const last = new Date(this.fullDatasetLatestTime)
      const totalDuration = last.getTime() - first.getTime()
      
      if (this.timelineStart && this.timelineEnd) {
        const startDate = new Date(this.timelineStart)
        const endDate = new Date(this.timelineEnd)
        
        this.selectedRangePercent.start = ((startDate.getTime() - first.getTime()) / totalDuration) * 100
        this.selectedRangePercent.end = ((endDate.getTime() - first.getTime()) / totalDuration) * 100
        
        // Ensure percentages are within bounds
        this.selectedRangePercent.start = Math.max(0, Math.min(100, this.selectedRangePercent.start))
        this.selectedRangePercent.end = Math.max(0, Math.min(100, this.selectedRangePercent.end))
        
        // Ensure minimum gap
        if (this.selectedRangePercent.end - this.selectedRangePercent.start < 5) {
          this.selectedRangePercent.end = Math.min(100, this.selectedRangePercent.start + 5)
        }
        
        console.log('📊 Timeline percentages updated:', {
          start: this.selectedRangePercent.start.toFixed(2) + '%',
          end: this.selectedRangePercent.end.toFixed(2) + '%',
          range: (this.selectedRangePercent.end - this.selectedRangePercent.start).toFixed(2) + '%',
          timelineStart: this.timelineStart,
          timelineEnd: this.timelineEnd
        })
      }
    },
    
    formatDateTimeLocal(date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      return `${year}-${month}-${day}T${hours}:${minutes}`
    },
    
    async updateCharts() {
      // Prevent multiple simultaneous chart updates
      if (this.chartUpdateInProgress) {
        this.pendingChartUpdate = true
        console.log('Chart update already in progress, queuing request')
        return
      }
      
      // Throttle check - prevent updates if too recent
      const now = Date.now()
      if (now - this.lastChartUpdateTime < this.chartUpdateThrottleMs) {
        console.log('🕐 Chart update throttled - too recent, skipping')
        return
      }
      
      // Set freeze state immediately
      this.chartUpdateInProgress = true
      this.isUpdatingCharts = true
      
      console.log('🔄 Starting chart update - timeline frozen')
      
      // Clear any existing timeout
      if (this.updateChartsTimeout) {
        clearTimeout(this.updateChartsTimeout)
        this.updateChartsTimeout = null
      }
      
      // Validate timeline dates before updating charts
      if (!this.timelineStart || !this.timelineEnd) {
        console.warn('Timeline dates not set, skipping chart update')
        this.chartUpdateInProgress = false
        this.isUpdatingCharts = false
        return
      }
      
      try {
        const startDate = new Date(this.timelineStart)
        const endDate = new Date(this.timelineEnd)
        
        console.log('Parsed timeline dates:', {
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          startDateValid: !isNaN(startDate.getTime()),
          endDateValid: !isNaN(endDate.getTime())
        })
        
        if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
          console.error('Invalid timeline dates:', { start: this.timelineStart, end: this.timelineEnd })
          return
        }
        
        if (startDate >= endDate) {
          console.warn('Start date must be before end date')
          return
        }
        
        // Check if we have filtered data
        const filteredData = this.filteredData
        console.log('Filtered data check:', {
          filteredDataExists: !!filteredData,
          filteredDataLength: filteredData?.length || 0,
          filteredDataType: typeof filteredData,
          isArray: Array.isArray(filteredData)
        })
        
        if (!filteredData || filteredData.length === 0) {
          console.warn('No filtered data available, skipping chart update')
          // Try to fetch data if we don't have any
          if (this.timelineStart && this.timelineEnd) {
            console.log('Attempting to fetch data for current timeline...')
            await this.fetchDataForTimeRange(this.timelineStart, this.timelineEnd)
            // Check again after fetching
            if (!this.filteredData || this.filteredData.length === 0) {
              console.warn('Still no data after fetching, skipping chart update')
              return
            }
          } else {
            return
          }
        }
        
        console.log('📊 Updating charts with', filteredData.length, 'data points')
        
        // Manually update the charts to ensure they display the data
        if (this.$refs.signalChart && this.$refs.signalChart.updateChart) {
          console.log('🔄 Manually updating signal chart')
          this.$refs.signalChart.updateChart()
        }
        if (this.$refs.deviceChart && this.$refs.deviceChart.updateChart) {
          console.log('🔄 Manually updating device chart')
          this.$refs.deviceChart.updateChart()
        }
        
        console.log('✅ Chart update completed successfully')
        
      } catch (error) {
        console.error('Error during chart update:', error)
      } finally {
        // Always reset the flags when charts are done updating
        console.log('🔄 Resetting freeze state - timeline unfrozen')
        this.chartUpdateInProgress = false
        this.isUpdatingCharts = false
        
        // CRITICAL: Clear checkbox processing status when charts are done
        if (this._checkboxProcessingStatus === 'Updating ⟳') {
          console.log('🧹 Clearing checkbox processing status after chart update')
          this._checkboxProcessingStatus = null
        }
        
        // Reset timeline adjustment flag to unfreeze checkbox
        if (this.isTimelineAdjusting) {
          this.isTimelineAdjusting = false
          console.log('🔓 Timeline adjustment completed - checkbox unfrozen')
        }
        
        // Update the last chart update timestamp for throttling
        this.lastChartUpdateTime = Date.now()
        
        // Reset the safety timer since charts updated successfully
        if (this.chartUpdateSafetyTimer) {
          clearTimeout(this.chartUpdateSafetyTimer)
          this.chartUpdateSafetyTimer = setTimeout(() => {
            this.checkChartUpdateSafety()
          }, 5000)
        }
        
        // Check if there's a pending update and process it
        if (this.pendingChartUpdate) {
          console.log('📋 Processing pending chart update')
          this.pendingChartUpdate = false
          // Use setTimeout to allow the current update to fully complete
          this.updateChartsTimeout = setTimeout(() => {
            this.updateCharts()
          }, 200)
        }
      }
    },
    
    // Debounced version to prevent rapid successive calls
    debouncedUpdateCharts() {
      // Guard: Don't update if component is unmounting
      if (this._isUnmounting) {
        console.log('🚫 Chart update blocked - component unmounting')
        return
      }
      
      console.log('🔍 debouncedUpdateCharts called - checking state:', {
        isInTransitionalState: this.isInTransitionalState,
        chartCreationComplete: this.chartCreationComplete,
        _checkboxProcessingStatus: this._checkboxProcessingStatus,
        isUpdatingCharts: this.isUpdatingCharts,
        chartUpdateInProgress: this.chartUpdateInProgress
      })
      
      // Use freeze guard to prevent chart updates when page is busy
      if (this.isInTransitionalState) {
        console.log('🚫 Chart update blocked - page is busy:', {
          isInTransitionalState: this.isInTransitionalState,
          chartCreationComplete: this.chartCreationComplete
        })
        
        // Clear checkbox processing status if the update is blocked
        if (this._checkboxProcessingStatus === 'Updating ⟳') {
          console.log('🧹 Clearing checkbox processing status - chart update blocked')
          this._checkboxProcessingStatus = null
        }
        
        return
      }
      
      // Throttle check - prevent updates if too recent
      const now = Date.now()
      if (now - this.lastChartUpdateTime < this.chartUpdateThrottleMs) {
        console.log('🕐 Chart update throttled - too recent, skipping')
        return
      }
      
      // Clear any existing timeout
      if (this.updateChartsTimeout) {
        console.log('🧹 Clearing existing updateChartsTimeout')
        clearTimeout(this.updateChartsTimeout)
        this.updateChartsTimeout = null
      }
      
      // Set a new timeout to update charts after a delay
      this.updateChartsTimeout = setTimeout(() => {
        // Clear the timeout reference first
        this.updateChartsTimeout = null
        
        // Double-check that we're still not updating before proceeding
        if (!this.isInTransitionalState) {
          console.log('🔄 Debounced chart update triggered - manually updating charts')
          // CRITICAL: We need to manually update charts when data changes
          this.manuallyUpdateCharts()
          console.log('✅ Charts manually updated')
        } else {
          console.log('Debounced update cancelled - page is busy')
          
          // If the update is cancelled, unfreeze the checkbox to prevent it from being stuck
          if (this._checkboxChangeInProgress) {
            this._checkboxChangeInProgress = false
            console.log('🔓 Checkbox unfrozen after cancelled chart update')
          }
          
          // Also clear the checkbox processing status if the update is cancelled
          if (this._checkboxProcessingStatus === 'Updating ⟳') {
            console.log('🧹 Clearing checkbox processing status - debounced update cancelled')
            this._checkboxProcessingStatus = null
          }
        }
      }, 1500) // Increased delay to 1500ms to prevent infinite loops
      
      // Debug logging for timeout creation
      console.log('⏰ updateChartsTimeout created:', {
        chartCreationComplete: this.chartCreationComplete,
        isInTransitionalState: this.isInTransitionalState
      })
    },

    // Method to clear checkbox processing status
    clearCheckboxProcessingStatus() {
      this._checkboxProcessingStatus = null
    },
    
    // Method to manually update charts when data changes using destroy/recreate approach
    async manuallyUpdateCharts() {
      // Guard: Don't update if component is unmounting
      if (this._isUnmounting) {
        console.log('🚫 Manual chart update blocked - component unmounting')
        return
      }
      
      try {
        // Update signal chart
        if (this.$refs.signalChart && this.$refs.signalChart.destroyChart && this.$refs.signalChart.$refs.baseChart) {
          await this.$refs.signalChart.destroyChart()
          await this.$refs.signalChart.$refs.baseChart.initializeChart()
        }
        
        // Update device chart
        if (this.$refs.deviceChart && this.$refs.deviceChart.destroyChart && this.$refs.deviceChart.$refs.baseChart) {
          await this.$refs.deviceChart.destroyChart()
          await this.$refs.deviceChart.$refs.baseChart.initializeChart()
        }
        
        // Mark chart creation as complete if it wasn't already
        if (!this.chartCreationComplete) {
          this.chartCreationComplete = true
          console.log('🔧 chartCreationComplete set to true in manuallyUpdateCharts')
        }
        
        // Reset transitional state flags after manual chart update
        this.forceResetTransitionalState()
      } catch (error) {
        console.error('Error manually updating charts:', error)
        // Reset flags even if there was an error
        this.forceResetTransitionalState()
      }
    },
    
    
    forceStopChartJS() {
      // Don't force stop during chart creation to avoid interference
      if (this.isUpdatingCharts || this.chartUpdateInProgress || this._settingInitialTimeline) {
        console.log('🛑 Skipping force stop during chart creation/update');
        return;
      }
      
      console.log('🛑 Force stopping all Chart.js operations');
      
      // Stop signal chart component operations
      if (this.$refs.signalChart) {
        try {
          this.$refs.signalChart.forceStopChart()
        } catch (e) {
          console.log('Signal chart stop error:', e);
        }
      }
      
      // Stop device chart component operations
      if (this.$refs.deviceChart) {
        try {
          this.$refs.deviceChart.forceStopChart()
        } catch (e) {
          console.log('Device chart stop error:', e);
        }
      }
      
      // Cancel any pending requestAnimationFrame calls globally
      if (window.requestAnimationFrame) {
        // Store the original function
        if (!window.__originalRequestAnimationFrame) {
          window.__originalRequestAnimationFrame = window.requestAnimationFrame;
        }
        
        // Override to block new animation frames temporarily
        window.requestAnimationFrame = () => {
          console.log('🛑 Blocked requestAnimationFrame during chart update');
          return 0;
        };
        
        // Restore after a delay
        setTimeout(() => {
          if (window.__originalRequestAnimationFrame) {
            window.requestAnimationFrame = window.__originalRequestAnimationFrame;
            console.log('🔓 Restored requestAnimationFrame');
          }
        }, 1000);
      }
      
      // CRITICAL: Clear any pending chart update timeout
      if (this.updateChartsTimeout) {
        console.log('🧹 Clearing updateChartsTimeout in forceStopChartJS')
        clearTimeout(this.updateChartsTimeout)
        this.updateChartsTimeout = null
      }
      
      // Clear the chart initialization queue to prevent conflicts
      if (this.$refs.signalChart && this.$refs.signalChart.clearChartQueue) {
        this.$refs.signalChart.clearChartQueue()
      }
      if (this.$refs.deviceChart && this.$refs.deviceChart.clearChartQueue) {
        this.$refs.deviceChart.clearChartQueue()
      }
      
      // Clear error states to allow chart updates to resume
      if (this.$refs.signalChart && this.$refs.signalChart.clearErrorState) {
        this.$refs.signalChart.clearErrorState()
      }
      if (this.$refs.deviceChart && this.$refs.deviceChart.clearErrorState) {
        this.$refs.deviceChart.clearErrorState()
      }
    },
    
    // Enhanced method to completely destroy and cleanup all charts (component-based)
    async destroyAllCharts() {
      console.log('🗑️ Destroying all charts...')
      
      try {
        // Destroy signal chart component
        if (this.$refs.signalChart) {
          try {
            await this.$refs.signalChart.destroyChart()
            console.log('✅ Signal chart destroyed')
          } catch (error) {
            console.warn('⚠️ Error destroying signal chart:', error)
          }
        }
        
        // Destroy device chart component
        if (this.$refs.deviceChart) {
          try {
            await this.$refs.deviceChart.destroyChart()
            console.log('✅ Device chart destroyed')
          } catch (error) {
            console.warn('⚠️ Error destroying device chart:', error)
          }
        }
        
        // Clear any remaining Chart.js instances
        if (window.Chart && window.Chart.instances) {
          Object.keys(window.Chart.instances).forEach(id => {
            try {
              const instance = window.Chart.instances[id]
              if (instance && instance.destroy) {
                instance.destroy()
                console.log(`🗑️ Destroyed Chart.js instance ${id}`)
              }
            } catch (error) {
              console.warn(`⚠️ Error destroying Chart.js instance ${id}:`, error)
            }
          })
        }
        
        // Reset canvas elements to ensure they're completely clean
        await this.resetCanvasElements()
        
        // Small delay to ensure cleanup completes
        await new Promise(resolve => setTimeout(resolve, 100))
        console.log('✅ All charts destroyed successfully')
        
      } catch (error) {
        console.error('❌ Error during chart destruction:', error)
      }
    },

    // Method to recover from chart corruption
    async recoverCharts() {
      console.log('🔄 Attempting chart recovery...')
      
      try {
        // Force stop any ongoing operations
        if (this.forceStopChartJS) {
          this.forceStopChartJS()
        }

        
        // Clear any pending timeouts
        if (this.updateChartsTimeout) {
          clearTimeout(this.updateChartsTimeout)
          this.updateChartsTimeout = null
        }
        
        // Reset all chart flags
        this.isUpdatingCharts = false
        this.chartUpdateInProgress = false

        this.isFetchingData = false
        this._settingInitialTimeline = false
        this.chartCreationComplete = false
        
        // Destroy existing charts
        await this.destroyAllCharts()
        
        // Wait a moment for cleanup
        await new Promise(resolve => setTimeout(resolve, 200))
        
        // Recreate charts
        console.log('🔄 Recreating charts after recovery...')
        if (this.createInitialCharts) {
          await this.createInitialCharts()
        } else {
          console.log('⚠️ createInitialCharts method not found, attempting manual recovery...')
          // Manual recovery: just reset state and let the component handle it
          this.chartCreationComplete = false
          this.isUpdatingCharts = false
          this.chartUpdateInProgress = false
        }
        
        console.log('✅ Chart recovery completed')
        return true
      } catch (error) {
        console.error('❌ Chart recovery failed:', error)
        return false
      }
    },
    
    // Reset canvas elements to ensure they're completely clean
    async resetCanvasElements() {
      console.log('🧹 Resetting canvas elements...')
      
      try {
        // Reset signal chart canvas
        if (this.$refs.signalChart) {
          const canvas = this.$refs.signalChart
          const ctx = canvas.getContext('2d')
          if (ctx) {
            ctx.clearRect(0, 0, canvas.width, canvas.height)
            // Reset canvas dimensions to force a complete refresh
            const originalWidth = canvas.width
            const originalHeight = canvas.height
            canvas.width = 0
            canvas.height = 0
            canvas.width = originalWidth
            canvas.height = originalHeight
            console.log('✅ Signal chart canvas reset')
          }
        }
        
        // Reset device chart canvas
        if (this.$refs.deviceChart) {
          const canvas = this.$refs.deviceChart
          const ctx = canvas.getContext('2d')
          if (ctx) {
            ctx.clearRect(0, 0, canvas.width, canvas.height)
            // Reset canvas dimensions to force a complete refresh
            const originalWidth = canvas.width
            const originalHeight = canvas.height
            canvas.width = 0
            canvas.height = 0
            canvas.width = originalWidth
            canvas.height = originalHeight
            console.log('✅ Device chart canvas reset')
          }
        }
        
        // Small delay to ensure canvas reset completes
        await new Promise(resolve => setTimeout(resolve, 50))
        
      } catch (error) {
        console.warn('⚠️ Error resetting canvas elements:', error)
      }
    },
    

    
    // Handle window blur event - stop all operations when user switches away
    handleWindowBlur() {
      console.log('Window blurred, stopping chart updates.')
      this.isUpdatingCharts = false
      this.chartUpdateInProgress = false
      this.pendingChartUpdate = false
      if (this.updateChartsTimeout) {
        clearTimeout(this.updateChartsTimeout)
        this.updateChartsTimeout = null
      }
    },
    
    // Handle window focus event - just log, don't trigger unnecessary updates
    handleWindowFocus() {
      // Guard: Don't handle if component is unmounting
      if (this._isUnmounting) {
        return
      }
      
      console.log('Window focused - no automatic chart updates needed.')
      // Charts are already up-to-date, no need to update when returning to the page
      // This prevents unnecessary processing and resource usage when switching tabs
      // Charts will only update when you actually interact with them (timeline, checkbox, etc.)
    },
    
    // Handle keyboard events - chart recovery on Ctrl+R
    handleKeyDown(event) {
      // Page refresh shortcut: Ctrl+R (or Cmd+R on Mac)
      if ((event.ctrlKey || event.metaKey) && event.key === 'r') {
        event.preventDefault()
        console.log('🔄 Page refresh shortcut triggered')
        this.refreshPage()
      }
    },
    
    // Handle visibility change events - stop operations when tab becomes hidden
    handleVisibilityChange() {
      if (document.hidden) {
        console.log('Tab hidden, stopping chart updates.')
        this.isUpdatingCharts = false
        this.chartUpdateInProgress = false
        this.pendingChartUpdate = false
        if (this.updateChartsTimeout) {
          clearTimeout(this.updateChartsTimeout)
          this.updateChartsTimeout = null
        }
      }
    },
    
    // Handle page unload events - ensure cleanup
    handleBeforeUnload() {
      console.log('Page unloading, cleaning up...')
      if (this.updateChartsTimeout) {
        clearTimeout(this.updateChartsTimeout)
        this.updateChartsTimeout = null
      }
    },
    
    // Handle clicks outside dropdown to close it
    handleClickOutside(event) {
      const dropdown = event.target.closest('.relative')
      if (!dropdown || !dropdown.contains(event.target)) {
        this.showCurveDropdown = false
      }
    },
    

    
    // Periodic check to ensure freeze state consistency
    checkFreezeStateConsistency() {
      // Guard: Don't run if component is unmounting
      if (this._isUnmounting) {
        return
      }
      
      // Skip checks during chart creation to avoid interference
      if (this.isInTransitionalState) {
        return
      }
      
      // If charts are updating but we're not in the expected state, fix it
      if (this.isUpdatingCharts && !this.chartUpdateInProgress) {
        console.warn('Inconsistent freeze state detected, fixing...')
        this.chartUpdateInProgress = true
      }
      
      // If we're not updating charts but still have the flag set, reset it
      if (!this.isUpdatingCharts && this.chartUpdateInProgress) {
        console.warn('Inconsistent freeze state detected, fixing...')
        this.chartUpdateInProgress = false
      }
      

    },
    



    
    // Method to validate and log the final timeline state
    validateTimelineState() {
      console.log('🔍 Validating final timeline state...')
      
      try {
        if (!this.timelineStart || !this.timelineEnd) {
          console.warn('❌ Timeline dates not set')
          return false
        }
        
        const startDate = new Date(this.timelineStart)
        const endDate = new Date(this.timelineEnd)
        
        if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
          console.warn('❌ Invalid timeline dates')
          return false
        }
        
        if (startDate >= endDate) {
          console.warn('❌ Start date must be before end date')
          
          // Fix by swapping the times
          this.timelineStart = endDate.toISOString().replace('Z', '').replace(/\.\d{3}/, '')
          this.timelineEnd = startDate.toISOString().replace('Z', '').replace(/\.\d{3}/, '')
          
          console.log('🔄 Timeline times swapped to fix validation error')
          
          // Also update the timelineRange to match
          if (this.timelineRange) {
            this.timelineRange.start = endDate
            this.timelineRange.end = startDate
            this.timelineRange.mid = new Date(endDate.getTime() + (startDate.getTime() - endDate.getTime()) / 2)
          }
          
          return false
        }
        
        // Ensure timelineRange is consistent with timelineStart/End
        if (this.timelineRange) {
          const rangeStart = new Date(this.timelineStart)
          const rangeEnd = new Date(this.timelineEnd)
          
          if (this.timelineRange.start.getTime() !== rangeStart.getTime() || 
              this.timelineRange.end.getTime() !== rangeEnd.getTime()) {
            console.log('🔄 Updating timelineRange to match timelineStart/End')
            this.timelineRange.start = rangeStart
            this.timelineRange.end = rangeEnd
            this.timelineRange.mid = new Date(rangeStart.getTime() + (rangeEnd.getTime() - rangeStart.getTime()) / 2)
          }
        }
        
        const durationHours = (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60)
        
        console.log('✅ Timeline validation passed:', {
          start: startDate.toISOString(),
          end: endDate.toISOString(),
          duration: durationHours.toFixed(2) + ' hours',
          startPercent: this.selectedRangePercent.start.toFixed(2) + '%',
          endPercent: this.selectedRangePercent.end.toFixed(2) + '%',
          rangePercent: (this.selectedRangePercent.end - this.selectedRangePercent.start).toFixed(2) + '%'
        })
        
        return true
      } catch (error) {
        console.error('❌ Timeline validation error:', error)
        return false
      }
    },
    
    // Method to update the visual timeline display
    updateTimelineDisplay() {
      console.log('🎨 Updating timeline display...')
      
      // This method should work during initial setup for curve-based positioning
      if (this._settingInitialTimeline) {
        console.log('🎨 updateTimelineDisplay working during initial setup for curve positioning')
      }
      
      // Safeguard: ensure percentage range object exists before accessing
      if (!this.selectedRangePercent || typeof this.selectedRangePercent !== 'object') {
        console.warn('🎨 Timeline percentages object missing; skipping display update')
        return
      }
      
      try {
        // Ensure the timeline percentages are correctly set
        if (this.selectedRangePercent.start !== undefined && this.selectedRangePercent.end !== undefined) {
          console.log('🎨 Timeline display updated:', {
            startPointer: this.selectedRangePercent.start.toFixed(2) + '%',
            endPointer: this.selectedRangePercent.end.toFixed(2) + '%',
            range: (this.selectedRangePercent.end - this.selectedRangePercent.start).toFixed(2) + '%'
          })
          
          // Force a reactive update to ensure the visual pointers move
          this.$forceUpdate()
        } else {
          console.warn('🎨 Timeline percentages not set for display update')
        }
      } catch (error) {
        console.error('🎨 Error updating timeline display:', error)
      }
    },
    
    // Method to force update timeline percentages even during initial setup
    forceUpdateTimelinePercentages() {
      console.log('🔧 Force updating timeline percentages...')
      
      // This method is specifically designed to work during initial setup
      if (this._settingInitialTimeline) {
        console.log('🔧 forceUpdateTimelinePercentages working during initial setup')
      }
      
      if (!this.processingResults?.data_metadata?.start_time || !this.processingResults?.data_metadata?.end_time) {
        console.warn('No data metadata available for percentage calculation')
        return
      }
      
      const first = new Date(this.processingResults.data_metadata.start_time)
      const last = new Date(this.processingResults.data_metadata.end_time)
      const totalDuration = last.getTime() - first.getTime()
      
      if (this.timelineStart && this.timelineEnd) {
        const startDate = new Date(this.timelineStart)
        const endDate = new Date(this.timelineEnd)
        
        // Calculate percentages based on the curve-based timeline
        this.selectedRangePercent.start = ((startDate.getTime() - first.getTime()) / totalDuration) * 100
        this.selectedRangePercent.end = ((endDate.getTime() - first.getTime()) / totalDuration) * 100
        
        // Ensure percentages are within bounds
        this.selectedRangePercent.start = Math.max(0, Math.min(100, this.selectedRangePercent.start))
        this.selectedRangePercent.end = Math.max(0, Math.min(100, this.selectedRangePercent.end))
        
        // Ensure minimum gap
        if (this.selectedRangePercent.end - this.selectedRangePercent.start < 5) {
          this.selectedRangePercent.end = Math.min(100, this.selectedRangePercent.start + 5)
        }
        
        console.log('🔧 Timeline percentages force-updated:', {
          start: this.selectedRangePercent.start.toFixed(2) + '%',
          end: this.selectedRangePercent.end.toFixed(2) + '%',
          range: (this.selectedRangePercent.end - this.selectedRangePercent.start).toFixed(2) + '%',
          timelineStart: this.timelineStart,
          timelineEnd: this.timelineEnd,
          dataFirst: first.toISOString(),
          dataLast: last.toISOString(),
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          totalDurationHours: (totalDuration / (1000 * 60 * 60)).toFixed(2)
        })
        
        // Force a reactive update to ensure the visual pointers move
        this.$forceUpdate()
      } else {
        console.warn('Timeline dates not set for percentage calculation')
      }
    },
    
    // Method to update x-axis limits when timeline changes
    updateChartXAxisLimits(override = false) {
      // Safety check - ensure timeline dates are valid
      if (!this.timelineStart || !this.timelineEnd) {
        console.log('Timeline dates not set, skipping x-axis update')
        return
      }
      
      // Use freeze guard to prevent x-axis updates when page is busy
      // But allow updates during initial setup
      if (!override && this.isInTransitionalState && this.chartCreationComplete) {
        console.log('🔒 updateChartXAxisLimits blocked - page is busy')
        return
      }
      
      // Simple recursion guard
      if (this._updatingXAxis) {
        console.log('🔒 updateChartXAxisLimits blocked - recursion guard active')
        return
      }
      
      this._updatingXAxis = true
      
      // Allow updates during initial timeline setup for curve-based positioning
      if (this._settingInitialTimeline) {
        console.log('🔍 updateChartXAxisLimits allowed during initial setup for curve positioning')
        // But only if charts are actually ready and stable
        if (!this.$refs.signalChart || !this.$refs.deviceChart || !this.chartCreationComplete) {
          console.log('🔍 Charts not yet ready, skipping x-axis update')
          this._updatingXAxis = false
          return
        }
      }
      
      try {
        const startDate = new Date(this.timelineStart)
        const endDate = new Date(this.timelineEnd)
        
        if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
          console.warn('Invalid timeline dates for x-axis update:', { start: this.timelineStart, end: this.timelineEnd })
          return
        }
        
        if (startDate >= endDate) {
          console.warn('Start date must be before end date for x-axis update')
          return
        }
        
        // Update signal chart x-axis (component-based approach)
        if (this.$refs.signalChart && this.$refs.signalChart.getChart && this.chartCreationComplete) {
          const signalChart = this.$refs.signalChart.getChart()
          if (signalChart && signalChart.options && signalChart.options.scales && signalChart.options.scales.x) {
            signalChart.options.scales.x.min = startDate
            signalChart.options.scales.x.max = endDate
            // Don't call chart.update() here - it causes stack overflow due to Vue reactivity
            // The chart components will handle updates via their computed properties
            console.log('Signal chart x-axis limits set:', { min: startDate, max: endDate })
          }
        }
        
        // Update device chart x-axis (component-based approach)
        if (this.$refs.deviceChart && this.$refs.deviceChart.getChart && this.chartCreationComplete) {
          const deviceChart = this.$refs.deviceChart.getChart()
          if (deviceChart && deviceChart.options && deviceChart.options.scales && deviceChart.options.scales.x) {
            deviceChart.options.scales.x.min = startDate
            deviceChart.options.scales.x.max = endDate
            // Don't call chart.update() here - it causes stack overflow due to Vue reactivity
            // The chart components will handle updates via their computed properties
            console.log('Device chart x-axis limits set:', { min: startDate, max: endDate })
          }
        }
      } catch (error) {
        console.error('Error updating chart x-axis limits:', error)
      } finally {
        // Always clear the recursion guard
        this._updatingXAxis = false
      }
    },
    
        formatTimeDisplay(date) {
      if (!date) return '--'
      // API returns raw time, display as-is without timezone conversion
      const dateObj = date instanceof Date ? date : new Date(date)
      return dateObj.toLocaleTimeString('en-US', { 
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      })
    },

    formatDateTimeDisplay(date) {
      if (!date) return ''
      // API returns raw time, display as-is without timezone conversion
      const dateObj = new Date(date)
      return dateObj.toLocaleDateString('en-US') + ' ' + dateObj.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })
    },
    
    // Helper method to format timestamp for API requests (no timezone conversion)
    formatTimestampForApi(timestamp) {
      // If timestamp is already a timezone-naive string, return it as-is
      if (typeof timestamp === 'string' && !timestamp.includes('Z') && !timestamp.includes('+')) {
        return timestamp
      }
      // Otherwise, convert to timezone-naive string
      const date = new Date(timestamp)
      return date.toISOString().replace('Z', '').replace(/\.\d{3}/, '')
    },

    // New method to fetch data from server for current time range
    async fetchDataForTimeRange(startTime, endTime) {
      // Guard: Don't fetch if component is unmounting
      if (this._isUnmounting) {
        console.log('🚫 Data fetch blocked - component unmounting')
        return null
      }
      
      try {
        // Use freeze guard to prevent data fetching when page is busy
        // But allow initial data fetching during setup
        if (this.isInTransitionalState && this.chartCreationComplete) {
          console.log('🚫 Data fetch blocked - page is busy')
          return null
        }
        
        // CRITICAL: Use raw timezone-naive strings directly (no Date object conversion)
        // The startTime and endTime are already timezone-naive strings from timeline state
        
        // Validate that we have valid timezone-naive strings
        if (!startTime || !endTime || typeof startTime !== 'string' || typeof endTime !== 'string') {
          console.error('❌ Invalid timeline dates detected:', { startTime, endTime })
          return null
        }
        
        // Check cache first (Phase 1 optimization)
        const cacheKey = `${startTime}|${endTime}`
        const cachedData = this.timeRangeDataCache.get(cacheKey)
        if (cachedData) {
          return cachedData
        }
        
        // Parse as Date objects only for validation and calculations
        const startDate = new Date(startTime)
        const endDate = new Date(endTime)
        
        // Check if dates are valid
        if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
          console.error('❌ Invalid timeline dates detected:', { startTime, endTime })
          return null
        }
        
        // Check if start is after end and fix if needed
        if (startDate >= endDate) {
          console.error('❌ Invalid time range: start time is after or equal to end time')
          console.error('Original:', { start: startTime, end: endTime })
          
          // Fix by swapping the times (keep as timezone-naive strings)
          const temp = startTime
          startTime = endTime
          endTime = temp
          
          // Update the component state to match
          this.timelineStart = startTime
          this.timelineEnd = endTime
          
          console.log('🔄 Time range fixed by swapping:', { start: startTime, end: endTime })
        }
        
        // Check 24-hour limit
        const timeDiffHours = (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60)
        let adjustedEndTime = endTime
        if (timeDiffHours > this.maxTimeRangeHours) {
          console.warn(`Time range ${timeDiffHours.toFixed(1)} hours exceeds ${this.maxTimeRangeHours}-hour limit`)
          // Adjust end time to respect limit
          endDate.setTime(startDate.getTime() + (this.maxTimeRangeHours * 60 * 60 * 1000))
          // Convert adjusted endDate back to timezone-naive ISO string (YYYY-MM-DDTHH:MM:SS)
          const adjustedYear = endDate.getFullYear()
          const adjustedMonth = String(endDate.getMonth() + 1).padStart(2, '0')
          const adjustedDay = String(endDate.getDate()).padStart(2, '0')
          const adjustedHours = String(endDate.getHours()).padStart(2, '0')
          const adjustedMinutes = String(endDate.getMinutes()).padStart(2, '0')
          const adjustedSeconds = String(endDate.getSeconds()).padStart(2, '0')
          adjustedEndTime = `${adjustedYear}-${adjustedMonth}-${adjustedDay}T${adjustedHours}:${adjustedMinutes}:${adjustedSeconds}`
          console.log(`Adjusted end time to respect ${this.maxTimeRangeHours}-hour limit:`, adjustedEndTime)
        }
        
        // Check if we have the required metadata (either session_id OR s3_key)
        if (!this.processingResults?.session_id && !this.processingResults?.s3_key) {
          console.error('No session ID or S3 key available for data fetching')
          return null
        }
        
        // Prevent multiple simultaneous fetches
        if (this.isFetchingData) {
          console.log('Data fetch already in progress, skipping')
          return null
        }
        
        this.isFetchingData = true
        
        // Note: Removed blocking server check - proceed with data fetch attempt
        
        // Send raw timezone-naive timestamps to backend (no UTC conversion)
        // Use the original timezone-naive strings directly (or adjusted endTime if limit was exceeded)
        const startTimeISO = startTime
        const endTimeISO = adjustedEndTime
        
        // Use s3_key if available (for prior results), otherwise use session_id (for active sessions)
        // Phase 1: Include columns parameter for optimization
        const requestPayload = {
          start_time: startTimeISO,
          end_time: endTimeISO,
          columns: ALL_CHART_COLUMNS  // Request only columns needed for charts (~15 cols vs ~50 cols)
        }
        
        if (this.processingResults.s3_key) {
          // Prior results: use s3_key for direct S3 access
          requestPayload.s3_key = this.processingResults.s3_key
          console.log('📦 Using s3_key for data fetch (prior result):', this.processingResults.s3_key)
        } else if (this.processingResults.session_id) {
          // Active session: use session_id
          requestPayload.session_id = this.processingResults.session_id
          console.log('📦 Using session_id for data fetch (active session):', this.processingResults.session_id)
        } else {
          console.error('❌ No s3_key or session_id available for data fetch')
          this.isFetchingData = false
          return null
        }
        
        console.log('🔍 Requesting data from server:', {
          session_id: this.processingResults.session_id,
          s3_key: this.processingResults.s3_key,
          start_time: startTimeISO,
          end_time: endTimeISO,
          requestedDurationHours: ((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60)).toFixed(2),
          timelineStart: this.timelineStart,
          timelineEnd: this.timelineEnd,
          hasCurveTimelineMap: Object.keys(this.curveTimelineMap).length > 0,
          curveTimelineMapKeys: Object.keys(this.curveTimelineMap)
        })
        
        // Debug: Show what we're requesting vs what the curve timeline map has
        if (Object.keys(this.curveTimelineMap).length > 0) {
          const firstCurve = this.curveTimelineMap[Object.keys(this.curveTimelineMap)[0]]
          console.log('🎯 Curve Timeline Map Debug:', {
            curveStart: firstCurve.startDate,
            curveEnd: firstCurve.endDate,
            curveStartFormatted: firstCurve.startFormatted,
            curveEndFormatted: firstCurve.endFormatted,
            requestedStart: startTimeISO,
            requestedEnd: endTimeISO
          })
        }
        
        let response
        try {
          response = await axios.post(buildApiUrl(API_ENDPOINTS.GET_DATA_FOR_TIME_RANGE), requestPayload, {
            timeout: 30000, // 30 second timeout
            headers: {
              'Content-Type': 'application/json'
            }
          })
        } catch (error) {
          // Handle network errors, timeouts, etc.
          console.error('❌ Network error fetching data:', {
            message: error.message,
            code: error.code,
            response: error.response?.data,
            status: error.response?.status,
            statusText: error.response?.statusText,
            requestUrl: buildApiUrl(API_ENDPOINTS.GET_DATA_FOR_TIME_RANGE),
            requestPayload: requestPayload
          })
          
          // If we have a response with error data, try to extract the message
          if (error.response?.data) {
            const errorMessage = error.response.data.message || error.response.data.error || JSON.stringify(error.response.data)
            console.error('❌ Server error response:', errorMessage)
            throw new Error(errorMessage)
          }
          
          // Network/timeout error
          throw new Error(error.message || 'Failed to fetch data from server')
        }
        
        // CRITICAL: Parse response.data if it's a JSON string (API Gateway sometimes returns stringified JSON)
        let responseData = response.data
        if (typeof responseData === 'string') {
          try {
            responseData = JSON.parse(responseData)
            console.log('🔧 Parsed JSON string response')
          } catch (parseError) {
            console.error('❌ Failed to parse response data as JSON:', parseError)
            console.error('❌ Raw response data:', responseData.substring(0, 200))
            throw new Error('Invalid JSON response from server')
          }
        }
        
        if (responseData.success) {
          const fetchedData = responseData.data
          console.log('📊 Data fetched:', fetchedData.total_rows, 'rows')
          
          // Debug: Show the actual data range returned by the API
          if (fetchedData.filtered_data && fetchedData.filtered_data.length > 0) {
            const firstRow = fetchedData.filtered_data[0]
            const lastRow = fetchedData.filtered_data[fetchedData.filtered_data.length - 1]
            console.log('📊 API Response Data Range:', {
              totalRows: fetchedData.filtered_data.length,
              firstRowDateTime: firstRow.datetime,
              lastRowDateTime: lastRow.datetime,
              firstRowTAC: firstRow.tac,
              lastRowTAC: lastRow.tac,
              requestedStart: startTimeISO,
              requestedEnd: endTimeISO,
              dataSpansFrom: new Date(firstRow.datetime).toISOString(),
              dataSpansTo: new Date(lastRow.datetime).toISOString()
            })
            
            // Debug: Compare requested vs received range
            const requestedStartTime = new Date(startTimeISO).getTime()
            const requestedEndTime = new Date(endTimeISO).getTime()
            const receivedStartTime = new Date(firstRow.datetime).getTime()
            const receivedEndTime = new Date(lastRow.datetime).getTime()
            
            console.log('🔍 Requested vs Received Range Analysis:', {
              requestedDurationHours: ((requestedEndTime - requestedStartTime) / (1000 * 60 * 60)).toFixed(2),
              receivedDurationHours: ((receivedEndTime - receivedStartTime) / (1000 * 60 * 60)).toFixed(2),
              missingStartHours: ((receivedStartTime - requestedStartTime) / (1000 * 60 * 60)).toFixed(2),
              missingEndHours: ((requestedEndTime - receivedEndTime) / (1000 * 60 * 60)).toFixed(2),
              requestedStart: new Date(startTimeISO).toISOString(),
              requestedEnd: new Date(endTimeISO).toISOString(),
              receivedStart: new Date(firstRow.datetime).toISOString(),
              receivedEnd: new Date(lastRow.datetime).toISOString()
            })
          }
          
          // Store the fetched data
          this.currentTimeRangeData = normalizeWindowRows(fetchedData.filtered_data)
          this.lastFetchTime = Date.now()
          
          // Cache the fetched data (Phase 1 optimization)
          const cacheKey = `${startTimeISO}|${endTimeISO}`
          this.timeRangeDataCache.set(cacheKey, fetchedData.filtered_data)
          
          // Force update charts to reflect new data
          // Charts will be created with correct data in Phase 4
          // No need to force updates here since charts don't exist yet
          console.log('📊 Data ready for chart creation in Phase 4')
          
          return fetchedData.filtered_data
        } else {
          // Log full response for debugging
          console.error('❌ Failed to fetch data - server returned success=false:', {
            responseData: responseData,
            message: responseData?.message,
            error: responseData?.error,
            status: response.status,
            statusText: response.statusText,
            requestPayload: requestPayload
          })
          
          const errorMessage = responseData?.message || responseData?.error || 'Unknown error from server'
          console.error('Failed to fetch data:', errorMessage)
          
          // Fallback: try to use any locally available data if server fetch fails
          if (this.processingResults?.processed_dataset?.length > 0) {
            console.log('Falling back to locally available data for charts')
            // Filter local data to the requested time range
            const localData = this.processingResults.processed_dataset.filter(row => {
              const rowDate = new Date(row.datetime)
              return rowDate >= startDate && rowDate <= endDate
            })
            
            if (localData.length > 0) {
              console.log(`Using ${localData.length} local data points as fallback`)
              this.currentTimeRangeData = localData
              
                        // Charts will automatically update via Vue reactivity when data changes
          console.log('📊 Fallback data updated - charts will re-render automatically')
              
              return localData
            }
          }
          
          return null
        }
        
      } catch (error) {
        console.error('Error fetching data for time range:', error)
        
        // Log more detailed error information
        if (error.response) {
          // Server responded with error status
          console.error('Server error response:', {
            status: error.response.status,
            statusText: error.response.statusText,
            data: error.response.data
          })
        } else if (error.request) {
          // Request was made but no response received
          console.error('No response received:', error.request)
        } else {
          // Something else happened
          console.error('Error details:', error.message)
        }
        
        return null
      } finally {
        this.isFetchingData = false
        
        // Reset timeline adjustment flag if data fetching failed
        if (this.isTimelineAdjusting && !this.currentTimeRangeData?.length) {
          this.isTimelineAdjusting = false
          console.log('🔓 Timeline adjustment failed - checkbox unfrozen')
        }
      }
    },
    
    // Check server connection status (non-blocking)
    async checkServerStatus() {
      try {
        await axios.get(buildApiUrl(API_ENDPOINTS.CURRENT_SESSION_INFO), { timeout: 5000 })
        this.serverStatus = 'connected'
        console.log('✅ Server connection confirmed')
      } catch (error) {
        this.serverStatus = 'disconnected'
        // Silent failure - don't log errors for missing/inaccessible endpoints
        // This is just a health check, not critical for functionality
      }
    },

    // Method to fetch the full dataset timeline range (just earliest/latest timestamps)
    async fetchDatasetTimelineRange() {
      console.log('=' * 80)
      console.log('🕐 fetchDatasetTimelineRange() called')
      console.log('🕐 Current state:', {
        hasProcessingResults: !!this.processingResults,
        sessionId: this.processingResults?.session_id,
        s3Key: this.processingResults?.s3_key,
        processingResultsKeys: this.processingResults ? Object.keys(this.processingResults) : []
      })
      
      if (!this.processingResults?.session_id && !this.processingResults?.s3_key) {
        console.warn('❌ No session ID or s3_key available for timeline range fetch')
        console.log('=' * 80)
        return null
      }
      
      try {
        const apiUrl = buildApiUrl(API_ENDPOINTS.GET_DATASET_TIMELINE_RANGE)
        
        // Use s3_key if available (for prior results), otherwise use session_id (for active sessions)
        const requestData = {}
        if (this.processingResults.s3_key) {
          // Prior results: use s3_key for direct S3 access
          requestData.s3_key = this.processingResults.s3_key
          console.log('📦 Using s3_key for timeline range (prior result):', this.processingResults.s3_key)
        } else if (this.processingResults.session_id) {
          // Active session: use session_id
          requestData.session_id = this.processingResults.session_id
          console.log('📦 Using session_id for timeline range (active session):', this.processingResults.session_id)
        }
        
        console.log('🕐 Making API call:', {
          url: apiUrl,
          method: 'POST',
          data: requestData,
          sessionId: this.processingResults.session_id,
          s3Key: this.processingResults.s3_key
        })
        
        console.log('🕐 API_ENDPOINTS.GET_DATASET_TIMELINE_RANGE:', API_ENDPOINTS.GET_DATASET_TIMELINE_RANGE)
        console.log('🕐 buildApiUrl result:', apiUrl)
        
        const response = await axios.post(apiUrl, requestData, {
          timeout: 30000, // 30 second timeout (optimized endpoint uses metadata fast path, but allow time for fallback)
          headers: {
            'Content-Type': 'application/json'
          }
        })
        
        console.log('🕐 API response received:', {
          status: response.status,
          statusText: response.statusText,
          headers: response.headers,
          data: response.data
        })
        
        // CRITICAL: Parse response.data if it's a JSON string (API Gateway sometimes returns stringified JSON)
        let responseData = response.data
        if (typeof responseData === 'string') {
          try {
            responseData = JSON.parse(responseData)
            console.log('🔧 Parsed JSON string response for timeline range')
          } catch (parseError) {
            console.error('❌ Failed to parse timeline range response as JSON:', parseError)
            console.error('❌ Raw response data:', responseData.substring(0, 200))
            return null
          }
        }
        
        if (responseData.success) {
          const timelineRange = responseData.data
          console.log('✅ Dataset timeline range fetched successfully:', timelineRange)
          console.log('✅ Response structure:', {
            success: responseData.success,
            hasData: !!responseData.data,
            dataKeys: responseData.data ? Object.keys(responseData.data) : [],
            earliest: responseData.data?.earliest,
            latest: responseData.data?.latest
          })
          console.log('=' * 80)
          return timelineRange
        } else {
          console.warn('⚠️ API returned success: false:', {
            message: responseData.message,
            fullResponse: responseData
          })
          console.log('=' * 80)
          return null
        }
      } catch (error) {
        console.error('❌ Error fetching timeline range:', {
          message: error.message,
          code: error.code,
          response: error.response,
          request: error.request,
          config: error.config
        })
        
        if (error.response) {
          // Server responded with error status
          console.error('❌ Server error response:', {
            status: error.response.status,
            statusText: error.response.statusText,
            data: error.response.data,
            headers: error.response.headers
          })
        } else if (error.request) {
          // Request was made but no response received
          console.error('❌ No response received:', {
            request: error.request,
            message: 'Request was made but no response was received'
          })
        } else {
          // Something else happened
          console.error('❌ Request setup error:', {
            message: error.message,
            stack: error.stack
          })
        }
        
        console.log('=' * 80)
        return null
      }
    },
    
    // Safety check for stuck chart updates - reloads page if charts are stuck for too long
    checkChartUpdateSafety() {
      // Guard: Don't run if component is unmounting
      if (this._isUnmounting) {
        return
      }
      
      // Check if charts are stuck in updating state
      if (this.isUpdatingCharts || this.chartUpdateInProgress) {
        console.warn('Charts appear to be stuck in updating state for 5+ seconds')
        console.warn('Current state:', {
          isUpdatingCharts: this.isUpdatingCharts,
          chartUpdateInProgress: this.chartUpdateInProgress,
          chartCreationComplete: this.chartCreationComplete,
          updateChartsTimeout: !!this.updateChartsTimeout,
          isInTransitionalState: this.isInTransitionalState
        })
        
        // Force stop any ongoing operations
        this.forceStopChartJS()

        
        // Clear any pending timeouts
        if (this.updateChartsTimeout) {
          clearTimeout(this.updateChartsTimeout)
          this.updateChartsTimeout = null
        }
        
        // Reset all flags
        this.isUpdatingCharts = false
        this.chartUpdateInProgress = false

        this.isFetchingData = false
        this._settingInitialTimeline = false
        
        // Show user notification before reload
        if (confirm('Charts appear to be stuck in an updating state. The page will reload to restore functionality. Continue?')) {
          window.location.reload()
        } else {
          // Try to manually recover by resetting state
          this.$nextTick(() => {
            this.createInitialCharts()
          })
        }
      } else {
        // Reset the safety timer for the next check (only if not unmounting)
        if (!this._isUnmounting) {
          this.chartUpdateSafetyTimer = setTimeout(() => {
            this.checkChartUpdateSafety()
          }, 5000)
        }
      }
    },
    
    // Set up global error handling for Chart.js errors
    setupChartErrorHandling() {
      // Store handler references so they can be removed later
      this._chartErrorHandler = (event) => {
        // Guard: Don't handle errors if component is unmounting
        if (this._isUnmounting) {
          return
        }
        
        // Check if this is a Chart.js related error
        if (event.error && event.error.message && 
            (event.error.message.includes('Cannot read properties of null') || 
             event.error.message.includes('Cannot read properties of undefined') ||
             event.error.message.includes('save') ||
             event.error.message.includes('clipArea') ||
             event.error.message.includes('chartArea') ||
             event.error.message.includes('includes') ||
             event.error.message.includes('Chart.js'))) {
          
          console.warn('⚠️ Chart.js error detected:', event.error.message)
          console.warn('⚠️ Error details:', {
            filename: event.filename,
            lineno: event.lineno,
            colno: event.colno,
            error: event.error
          })
          
          // Check if this is a context corruption error
          if ((event.error.message.includes('Cannot read properties of null') && 
               event.error.message.includes('save')) ||
              (event.error.message.includes('Cannot read properties of undefined') && 
               event.error.message.includes('includes'))) {
            console.warn('🚨 Chart context corruption detected - attempting recovery')
            this.handleChartContextError()
          }
          
          // Check if this is an eventFilter error (plugin-related)
          if (event.error.message.includes('Cannot read properties of undefined') && 
              event.error.message.includes('includes') &&
              (event.filename?.includes('chunk-') || event.filename?.includes('Chart.js'))) {
            console.warn('🚨 Chart plugin event error detected - attempting plugin recovery')
            this.handleChartPluginError()
          }
          
          // Check if this is a chartArea error (chart state corruption)
          if (event.error.message.includes('Cannot read properties of null') && 
              event.error.message.includes('chartArea')) {
            console.warn('🚨 Chart area error detected - attempting recovery')
            this.handleChartContextError()
          }
        }
      }
      
      // Add the error handler
      window.addEventListener('error', this._chartErrorHandler)
      
      // Unhandled promise rejection handler for async Chart.js errors
      this._chartRejectionHandler = (event) => {
        // Guard: Don't handle rejections if component is unmounting
        if (this._isUnmounting) {
          return
        }
        
        if (event.reason && event.reason.message && 
            (event.reason.message.includes('Chart.js') || 
             event.reason.message.includes('canvas') ||
             event.reason.message.includes('context'))) {
          
          console.warn('⚠️ Unhandled Chart.js promise rejection:', event.reason.message)
          this.handleChartContextError()
        }
      }
      
      // Add the rejection handler
      window.addEventListener('unhandledrejection', this._chartRejectionHandler)
    },
    
    // Handle Chart.js context corruption errors
    handleChartContextError() {
      // Guard: Don't handle errors if component is unmounting
      if (this._isUnmounting) {
        return
      }
      
      // Prevent multiple simultaneous recovery attempts
      if (this._recoveringFromContextError) {
        return
      }
      
      this._recoveringFromContextError = true
      
      try {
        // Force stop all Chart.js operations
        this.forceStopChartJS()

        
        // Clear any pending timeouts
        if (this.updateChartsTimeout) {
          clearTimeout(this.updateChartsTimeout)
          this.updateChartsTimeout = null
        }
        
        // Reset chart state
        this.isUpdatingCharts = false
        this.chartUpdateInProgress = false
        
        // Mark charts as invalid
        if (this.deviceChart) {
          this.deviceChart.destroyed = true
          this.deviceChart = null
        }
        
        // Attempt to recreate charts
        console.log('🔧 Attempting to recreate charts after context corruption')
        this.$nextTick(async () => {
          try {
            await this.createInitialCharts()
            console.log('✅ Charts successfully recreated after context corruption')
          } catch (error) {
            console.error('❌ Failed to recreate charts after context corruption:', error)
            // If recreation fails, suggest page reload
            if (confirm('Chart recovery failed. Would you like to reload the page to restore functionality?')) {
              window.location.reload()
            }
          } finally {
            this._recoveringFromContextError = false
          }
        })
        
      } catch (error) {
        console.error('❌ Error during chart context recovery:', error)
        this._recoveringFromContextError = false
        
        // Fallback to page reload
        if (confirm('Chart recovery encountered an error. Would you like to reload the page?')) {
          window.location.reload()
        }
      }
    },
    
    // Periodic chart health check to proactively detect issues (component-based)
    checkChartHealth() {
      // Guard: Don't run if component is unmounting
      if (this._isUnmounting) {
        return
      }
      
      // Only check if charts exist and are supposed to be working
      if (!this.chartCreationComplete || this._recoveringFromContextError) {
        return
      }
      
      try {
        // Check signal chart component health
        if (this.$refs.signalChart && !this.$refs.signalChart.isChartHealthy()) {
          console.warn('⚠️ Signal chart health check failed - context appears corrupted')
          this.handleChartContextError()
          return
        }
        
        // Check device chart component health
        if (this.$refs.deviceChart && !this.$refs.deviceChart.isChartHealthy()) {
          console.warn('⚠️ Device chart health check failed - context appears corrupted')
          this.handleChartContextError()
          return
        }
        
        // All checks passed - only log occasionally to reduce spam
        if (this.$refs.signalChart || this.$refs.deviceChart) {
          // Only log every 10th successful check to reduce console spam
          if (!this._healthCheckCount) this._healthCheckCount = 0
          this._healthCheckCount++
          if (this._healthCheckCount % 10 === 0) {
            console.log('✅ Chart health check passed (check #' + this._healthCheckCount + ')')
          }
        }
        
      } catch (error) {
        console.error('❌ Error during chart health check:', error)
        // If health check itself fails, trigger recovery
        this.handleChartContextError()
      }
    },
    
    // Handle Chart.js plugin errors specifically
    handleChartPluginError() {
      console.log('🔧 Handling Chart.js plugin error')
      
      // Try to fix plugin-related issues without full chart recreation
      try {
        // Get chart instances from components
        const signalChart = this.$refs.signalChart?.getChart()
        const deviceChart = this.$refs.deviceChart?.getChart()
        
        // Disable problematic plugins on both charts
        if (signalChart && signalChart.options && signalChart.options.plugins) {
          // Disable tooltip plugin which is often the source of eventFilter errors
          if (signalChart.options.plugins.tooltip) {
            signalChart.options.plugins.tooltip.enabled = false
            console.log('🔧 Disabled tooltip plugin on signal chart')
          }
          
          // Disable legend interactions which can cause eventFilter issues
          if (signalChart.options.plugins.legend) {
            signalChart.options.plugins.legend.onClick = null
            console.log('🔧 Disabled legend interactions on signal chart')
          }
        }
        
        if (deviceChart && deviceChart.options && deviceChart.options.plugins) {
          // Disable tooltip plugin
          if (deviceChart.options.plugins.tooltip) {
            deviceChart.options.plugins.tooltip.enabled = false
            console.log('🔧 Disabled tooltip plugin on device chart')
          }
          
          // Disable legend interactions
          if (deviceChart.options.plugins.legend) {
            deviceChart.options.plugins.legend.onClick = null
            console.log('🔧 Disabled legend interactions on device chart')
          }
        }
        
        // CRITICAL: Don't call chart.update() - it triggers Vue reactivity
        // Vue will automatically re-render the charts when the options change
        console.log('✅ Plugin options updated (no manual chart updates)')
        
        console.log('✅ Plugin error handled successfully')
        
      } catch (error) {
        console.error('❌ Failed to handle plugin error:', error)
        // Fall back to full chart recreation
        this.handleChartContextError()
      }
    },
    
    // Debounced version of data fetching
    debouncedFetchData(startTime, endTime) {
      // Clear existing timeout
      if (this.fetchDebounceTimeout) {
        clearTimeout(this.fetchDebounceTimeout)
      }
      
      // Set new timeout
      this.fetchDebounceTimeout = setTimeout(async () => {
        await this.fetchDataForTimeRange(startTime, endTime)
      }, 300) // 300ms debounce
    },
    
    // Checkbox plumbing removed from container

    // Method to force chart recovery when needed
    async forceChartRecovery() {
      console.log('🚨 Force chart recovery triggered')
      
      try {
        // Show user confirmation
        if (confirm('Charts appear to be corrupted. This will destroy and recreate all charts. Continue?')) {
          await this.recoverCharts()
        }
      } catch (error) {
        console.error('Force chart recovery failed:', error)
        // Fallback: reload the page
        if (confirm('Chart recovery failed. Reload the page to restore functionality?')) {
          window.location.reload()
        }
      }
    },

    // Method to check chart health and auto-recover if needed
    checkChartHealth() {
      try {
        // Check if charts are in a corrupted state
        const isCorrupted = (
          (this.isUpdatingCharts && this.chartUpdateInProgress) || // Both flags set
          (this.isUpdatingCharts && !this.chartCreationComplete) || // Updating but not complete
          (this.chartUpdateInProgress && !this.isUpdatingCharts) || // Progress but not updating
          (this.isDragging && (this.isUpdatingCharts || this.chartUpdateInProgress)) // Dragging while updating
        )
        
        if (isCorrupted) {
          console.warn('⚠️ Chart corruption detected, attempting auto-recovery...')
          this.recoverCharts()
        }
      } catch (error) {
        console.error('Error checking chart health:', error)
      }
    },

    // Method to refresh the entire page
    refreshPage() {
      console.log('🔄 Refreshing page...')
      
      // Show confirmation dialog
      if (confirm('This will refresh the page and restore all functionality. Any unsaved changes will be lost. Continue?')) {
        // Reload the page
        window.location.reload()
      }
    },
    
    // Event handlers for timeline component
    handleTimelineChange(timelineData) {
      // CRITICAL: Validate the timeline data before applying it
      let startTime = timelineData.startTime
      let endTime = timelineData.endTime
      
      // Check if dates are valid
      const startDate = new Date(startTime)
      const endDate = new Date(endTime)
      
      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        console.error('❌ Invalid timeline dates received from component:', { startTime, endTime })
        return
      }
      
      // Check if start is after end and fix if needed
      if (startDate >= endDate) {
        console.error('❌ Invalid timeline order received from component:', { start: startDate.toISOString(), end: endDate.toISOString() })
        
        // Fix by swapping the times
        [startTime, endTime] = [endTime, startTime]
        console.log('🔄 Timeline times swapped to fix invalid order from component')
      }
      
      // Normalize to timezone-naive strings for internal state and API to avoid UTC/local drift
      const formattedStart = this.formatTimestampForApi(startTime)
      const formattedEnd = this.formatTimestampForApi(endTime)
      this.timelineStart = formattedStart
      this.timelineEnd = formattedEnd
      
      // CRITICAL: Update selectedRangePercent to match the timeline change
      if (this.processingResults?.data_metadata?.start_time && this.processingResults?.data_metadata?.end_time) {
        const first = new Date(this.processingResults.data_metadata.start_time)
        const last = new Date(this.processingResults.data_metadata.end_time)
        const totalDuration = last.getTime() - first.getTime()
        
        const startDate = new Date(startTime)
        const endDate = new Date(endTime)
        
        this.selectedRangePercent.start = ((startDate.getTime() - first.getTime()) / totalDuration) * 100
        this.selectedRangePercent.end = ((endDate.getTime() - first.getTime()) / totalDuration) * 100
      }
      
      // Clear chart error states to allow updates after timeline changes
      this.clearChartErrorStates()
      
      // Handle different sources of timeline changes
      switch (timelineData.source) {
        case 'curve-selection':
          // Don't update selectedCurveIndex here as it would create a loop
          // The selectedCurveIndex is already set correctly by the curve selection event
          
          // Fetch data for the new timeline
          this.debouncedFetchData(formattedStart, formattedEnd)
          
          // Clear chart error states to allow updates
          this.clearChartErrorStates()
          
          // Update charts using new method
          this.onTimelineChange({ startTime: formattedStart, endTime: formattedEnd })
          break
          
        case 'reset-to-full':
          // Timeline was reset to full range
          this.selectedCurveIndex = ''
          this.selectedDayIndex = ''
          
          // Fetch data for the full range
          this.debouncedFetchData(formattedStart, formattedEnd)
          
          // Clear chart error states to allow updates
          this.clearChartErrorStates()
          
          // Force refresh charts to ensure they show the complete dataset
          this.$nextTick(() => {
            this.refreshChartsWithFullData()
          })
          
          // Update charts using new method
          this.onTimelineChange({ startTime: formattedStart, endTime: formattedEnd })
          break
          
        case 'day-selection':
          // Day selection timeline change received
          console.log('🎯 Day selection timeline change received:', {
            startTime,
            endTime,
            timelineData
          })
          
          // Don't update selectedDayIndex here as it would create a loop
          // The selectedDayIndex is already set correctly by the day selection event
          console.log('🎯 Skipping selectedDayIndex update to prevent loop')
          
          // Fetch data for the new timeline
          console.log('🎯 Fetching data for day selection timeline')
          this.debouncedFetchData(formattedStart, formattedEnd)
          
          // Clear chart error states to allow updates
          this.clearChartErrorStates()
          
          // Update charts using new method
          this.onTimelineChange({ startTime: formattedStart, endTime: formattedEnd })
          break
          
        case 'user-drag':
          // User manually adjusted timeline
          console.log('🔄 User drag detected - fetching data for new time range')
          
          // Fetch data for the dragged range (debounced to avoid rapid requests while dragging)
          this.debouncedFetchData(formattedStart, formattedEnd)
          
          // Clear chart error states to allow updates
          this.clearChartErrorStates()
          
          // Update charts using explicit handler so components re-render with new data
          this.onTimelineChange({ startTime: formattedStart, endTime: formattedEnd })
          break
      }
    },
    
    handleCurveSelectionChange(curveData) {
      console.log('🔄 Curve selection change event received:', curveData)
      
      // Update local curve selection state
      if (curveData.curveIndex === -1) {
        // Reset to no selection
        this.selectedCurveIndex = ''
      } else {
        this.selectedCurveIndex = curveData.curveIndex.toString()
      }
      
      // Update curve datasets in charts
      this.updateCurveDatasets()
    },
    
    handleDaySelectionChange(dayData) {
      console.log('🔄 Day selection change event received:', dayData)
      console.log('🔄 Day object details:', dayData.day)
      console.log('🔄 Day object keys:', dayData.day ? Object.keys(dayData.day) : 'No day object')
      
      // Update local day selection state
      if (dayData.dayIndex === -1) {
        // Reset to no selection
        this.selectedDayIndex = ''
      } else {
        this.selectedDayIndex = dayData.dayIndex.toString()
        console.log('🔄 Day selected:', dayData.dayIndex, 'using day-level data features')
      }
    },

    // Handle curve relabel events from TimelineComponent
    handleCurveRelabeled(eventData) {
      console.log('🔄 Curve relabeled event received:', eventData)
      
      try {
        // Update the curve features in processingResults
        if (this.processingResults?.curve_features && eventData.curveId !== undefined) {
          const curveIndex = this.processingResults.curve_features.findIndex(c => 
            (c.curve_id === eventData.curveId) || (this.processingResults.curve_features.indexOf(c) === eventData.curveId)
          )
          
          if (curveIndex !== -1) {
            // CRITICAL: Force Vue reactivity by creating new array and object references
            // This ensures TimelineComponent receives the updated curve data
            const updatedCurveFeatures = [...this.processingResults.curve_features]
            const curve = updatedCurveFeatures[curveIndex]
            const newCurveValid = eventData.newQuality ? 1 : 0
            
            // Recalculate REGION_VALID: REGION_VALID = 1 only when both CURVE_VALID == 1 AND PERIPHERY_VALID == 1
            const peripheryValid = curve.PERIPHERY_VALID !== undefined && curve.PERIPHERY_VALID !== null 
              ? Number(curve.PERIPHERY_VALID) 
              : 1
            const regionValid = (newCurveValid === 1 && peripheryValid === 1) ? 1 : 0
            
            updatedCurveFeatures[curveIndex] = {
              ...curve,
              CURVE_VALID: newCurveValid,
              REGION_VALID: regionValid,
              override_flags: eventData.override_flags,
              FLAG_override_decision: eventData.overrideDecision,
              override_note: eventData.note
            }
            
            // Update the curve features array (this triggers Vue reactivity)
            this.processingResults.curve_features = updatedCurveFeatures
            
            // Get the updated threshold_based_quality from sessionStorage (it was updated by TimelineComponent)
            const storedResults = sessionStorage.getItem('processingResults')
            if (storedResults) {
              const results = JSON.parse(storedResults)
              if (results.curve_features && results.curve_features[curveIndex]) {
                // Sync the threshold_based_quality field
                if (results.curve_features[curveIndex].threshold_based_quality !== undefined) {
                  updatedCurveFeatures[curveIndex].threshold_based_quality = results.curve_features[curveIndex].threshold_based_quality
                } else {
                  delete updatedCurveFeatures[curveIndex].threshold_based_quality
                }
              }
            }
            
            // ✨ UPDATE GLOBAL STATE - This makes changes visible across all components
            console.log('🌐 Updating curve features globally')
            this.updateCurveFeatures(updatedCurveFeatures)
            
            // Update curveTimelineMap if it exists - IMPORTANT: Update all override fields
            if (this.curveTimelineMap && this.curveTimelineMap[curveIndex]) {
              this.curveTimelineMap[curveIndex].valid = eventData.newQuality
              this.curveTimelineMap[curveIndex].CURVE_VALID = eventData.newQuality ? 1 : 0
              this.curveTimelineMap[curveIndex].override_flags = eventData.override_flags
              this.curveTimelineMap[curveIndex].FLAG_override_decision = eventData.overrideDecision
              this.curveTimelineMap[curveIndex].override_note = eventData.note
              
              // Sync threshold_based_quality with processingResults
              if (this.processingResults.curve_features[curveIndex].threshold_based_quality !== undefined) {
                this.curveTimelineMap[curveIndex].threshold_based_quality = this.processingResults.curve_features[curveIndex].threshold_based_quality
              } else {
                delete this.curveTimelineMap[curveIndex].threshold_based_quality
              }
              
              console.log('📊 Updated curveTimelineMap:', {
                curveIndex,
                valid: this.curveTimelineMap[curveIndex].valid,
                CURVE_VALID: this.curveTimelineMap[curveIndex].CURVE_VALID,
                override_flags: this.curveTimelineMap[curveIndex].override_flags,
                threshold_based_quality: this.curveTimelineMap[curveIndex].threshold_based_quality
              })
            }
            
            // Force Vue reactivity by triggering a re-render
            this.$forceUpdate()
            
            console.log(`✅ Updated curve ${eventData.curveId} validity to ${eventData.newQuality ? 'High' : 'Low'} Quality`)
            
            // Force update the curve datasets in charts
            this.updateCurveDatasets()
            
            // Show success message
            console.log(`✅ Curve ${eventData.curveId + 1} relabeled as ${eventData.newQuality ? 'High' : 'Low'} Quality`)
          }
        }
      } catch (error) {
        console.error('❌ Error handling curve relabel:', error)
        console.error('❌ Error updating curve after relabel')
      }
    },

    handleCurveRelabelError(eventData) {
      console.error('❌ Curve relabel error event received:', eventData)
      console.error(`❌ Failed to relabel curve: ${eventData.error}`)
    },

    handleCurveRelabelBackendError(eventData) {
      console.warn('⚠️ Curve relabel backend persistence error:', eventData)
      // Show a warning to the user that the change was made locally but couldn't be saved to the server
      // The frontend change is still visible, but it won't persist across sessions
      console.warn('⚠️ Manual quality label change was applied locally but could not be saved to the server. The change will be lost when you navigate away.')
    },

    
    // Method to retrieve day-level data using the day's start/end times
    async retrieveDayLevelData(day) {
      console.log('🔄 Retrieving day-level data for:', day)
      
      try {
        if (!day.start_time || !day.end_time) {
          console.warn('⚠️ Day data missing start_time or end_time:', day)
          return
        }
        
        // Use the day's start and end times to fetch data
        const startTime = day.start_time
        const endTime = day.end_time
        
        console.log('📊 Fetching data for day range:', {
          startTime,
          endTime,
          duration: ((new Date(endTime).getTime() - new Date(startTime).getTime()) / (1000 * 60 * 60)).toFixed(2) + ' hours'
        })
        
        // Call the existing data fetching method with day-level times
        await this.fetchDataForTimeRange(startTime, endTime)
        
        console.log('✅ Day-level data retrieved successfully')
        
      } catch (error) {
        console.error('❌ Error retrieving day-level data:', error)
      }
    },
    
    // Method to populate day data from processing results or API
    async populateDayData() {
      console.log('🔄 Populating day data...')
      console.log('🔍 Processing results keys:', Object.keys(this.processingResults || {}))
      console.log('🔍 day_level_data exists:', !!this.processingResults?.day_level_data)
      console.log('🔍 day_level_data length:', this.processingResults?.day_level_data?.length)
      
      try {
        // Check if we have processing results with day-level data
        if (this.processingResults?.day_level_data?.length > 0) {
          console.log('📊 Using day level data from processing results:', this.processingResults.day_level_data.length)
          console.log('🔍 First day data sample:', this.processingResults.day_level_data[0])
          console.log('🔍 First day data keys:', Object.keys(this.processingResults.day_level_data[0] || {}))
          this.dayData = this.processingResults.day_level_data
          return
        }
        
        // If no day features in processing results, try to generate day data from the dataset
        if (this.processingResults?.processed_dataset?.length > 0) {
          console.log('📊 Generating day data from processed dataset')
          console.log('🔍 processed_dataset length:', this.processingResults.processed_dataset.length)
          this.dayData = this.generateDayDataFromDataset()
          console.log('🔍 Generated dayData length:', this.dayData.length)
          return
        }
        
        // If no data available, set empty array
        console.log('⚠️ No data available for day generation')
        this.dayData = []
        
      } catch (error) {
        console.error('❌ Error populating day data:', error)
        this.dayData = []
      }
      
      // Initialize day selection if we're in day mode and have day data
      if (this.viewMode === 'day' && this.dayData && this.dayData.length > 0) {
        if (this.previousDayIndex !== '') {
          // Restore the previous day selection
          console.log('🔄 Restoring previous day selection:', this.previousDayIndex)
          this.selectedDayIndex = this.previousDayIndex
        } else {
          // Auto-select first day
          console.log('🔄 Auto-selecting first day')
          this.selectedDayIndex = '0'
        }
      }
    },
    
    // Method to generate day data from the processed dataset
    generateDayDataFromDataset() {
      console.log('🔄 Generating day data from dataset...')
      
      try {
        if (!this.processingResults?.processed_dataset?.length) {
          console.warn('⚠️ No processed dataset available for day generation')
          return []
        }
        
        const dataset = this.processingResults.processed_dataset
        const days = []
        
        // Group data by day (assuming timestamp column exists)
        const dayGroups = {}
        
        dataset.forEach(row => {
          if (row.timestamp) {
            const date = new Date(row.timestamp)
            const dayKey = date.toISOString().split('T')[0] // YYYY-MM-DD
            
            if (!dayGroups[dayKey]) {
              dayGroups[dayKey] = {
                start_time: null,
                end_time: null,
                data_points: []
              }
            }
            
            dayGroups[dayKey].data_points.push(row)
            
            // Track start and end times for each day
            if (!dayGroups[dayKey].start_time || new Date(row.timestamp) < new Date(dayGroups[dayKey].start_time)) {
              dayGroups[dayKey].start_time = row.timestamp
            }
            if (!dayGroups[dayKey].end_time || new Date(row.timestamp) > new Date(dayGroups[dayKey].end_time)) {
              dayGroups[dayKey].end_time = row.timestamp
            }
          }
        })
        
        // Convert day groups to day data format
        Object.keys(dayGroups).sort().forEach((dayKey, index) => {
          const dayGroup = dayGroups[dayKey]
          days.push({
            day_id: index + 1,
            start_time: dayGroup.start_time,
            end_time: dayGroup.end_time,
            duration: ((new Date(dayGroup.end_time).getTime() - new Date(dayGroup.start_time).getTime()) / (1000 * 60 * 60)).toFixed(2),
            data_points: dayGroup.data_points.length
          })
        })
        
        console.log('✅ Generated day data:', days.length, 'days')
        return days
        
      } catch (error) {
        console.error('❌ Error generating day data from dataset:', error)
        return []
      }
    },
    
    // REMOVED: Duplicate createInitialCharts method - using the one at line 1061 instead
    /*
    async createInitialCharts() {
      console.log('🎨 Creating initial charts...')
      console.log('🎨 Current state:', {
        chartCreationComplete: this.chartCreationComplete,
        isInTransitionalState: this.isInTransitionalState,
        isPageReadyForInteraction: this.isPageReadyForInteraction,
        hasData: !!this.currentTimeRangeData?.length,
        timelineStart: this.timelineStart,
        timelineEnd: this.timelineEnd
      })
      
      // Set flags to indicate chart creation is in progress
      this.chartUpdateInProgress = true
      this.isUpdatingCharts = true
      
      try {
        // The charts will be created automatically when the components mount
        // Just wait for them to be ready
        await this.$nextTick()
        
        // Mark that chart creation is complete
        this.chartCreationComplete = true
        console.log('🔧 chartCreationComplete set to true in createInitialCharts')
        
        // Clear checkbox processing status since charts are now complete
        this.clearCheckboxProcessingStatus()
        
        // CRITICAL: Unfreeze the checkbox now that charts are complete
        if (this._checkboxChangeInProgress) {
          this._checkboxChangeInProgress = false
          console.log('🔓 Checkbox unfrozen after chart creation')
        }
        
        // CRITICAL: Ensure timeline flag is reset to allow user interactions
        if (this._settingInitialTimeline) {
          this._settingInitialTimeline = false
          console.log('🔓 Timeline flag reset after chart creation - user interactions now allowed')
        }
        
        console.log('✅ Initial charts created successfully')
        
        // Enable auto-update on charts after they're stable
        this.$nextTick(() => {
          if (this.$refs.signalChart && this.$refs.signalChart.$el) {
            console.log('🔓 Enabling auto-update on signal chart')
          }
          if (this.$refs.deviceChart && this.$refs.deviceChart.$el) {
            console.log('🔓 Enabling auto-update on device chart')
          }
        })
        
        return { success: true }
        
        console.log('✅ Final state:', {
          chartCreationComplete: this.chartCreationComplete,
          isPageStatic: this.isPageStatic,
          isPageReadyForInteraction: this.isPageReadyForInteraction,
          _settingInitialTimeline: this._settingInitialTimeline
        })
        
        // Debug: Log the loading state status
        console.log('🔍 Loading state check:', {
          isInTransitionalState: this.isInTransitionalState,
          shouldShowStatusMessage: this.shouldShowStatusMessage,
          chartCreationComplete: this.chartCreationComplete,
          isUpdatingCharts: this.isUpdatingCharts,
          chartUpdateInProgress: this.chartUpdateInProgress,
          isFetchingData: this.isFetchingData,
          _settingInitialTimeline: this._settingInitialTimeline,
          pendingChartUpdate: this.pendingChartUpdate,
          updateChartsTimeout: this.updateChartsTimeout
        })
        
        // Force a reactive update to ensure computed properties are re-evaluated
        this.$nextTick(() => {
          console.log('🔍 After nextTick - Loading state check:', {
            isInTransitionalState: this.isInTransitionalState,
            shouldShowStatusMessage: this.shouldShowStatusMessage,
            chartCreationComplete: this.chartCreationComplete
          })
        })
      } catch (error) {
        console.error('Error creating initial charts:', error)
        // Ensure cleanup on any other errors
        await this.destroyAllCharts()
        return { success: false, error: error.message }
      } finally {
        // Always reset the flags when charts are done updating
        console.log('🔄 Resetting freeze state - timeline unfrozen')
        this.forceResetTransitionalState()
        
        // CRITICAL: Double-check timeline flag is reset
        if (this._settingInitialTimeline) {
          this._settingInitialTimeline = false
          console.log('🔓 Timeline flag reset in finally block - user interactions now allowed')
        }
        
        // CRITICAL: Unfreeze the checkbox now that charts are updated
        if (this._checkboxChangeInProgress) {
          this._checkboxChangeInProgress = false
          console.log('🔓 Checkbox unfrozen - user interactions now allowed')
        }
        
        // Update the last chart update timestamp for throttling
        this.lastChartUpdateTime = Date.now()
        
        // Reset the safety timer since initial charts were created successfully
        if (this.chartUpdateSafetyTimer) {
          clearTimeout(this.chartUpdateSafetyTimer)
          this.chartUpdateSafetyTimer = setTimeout(() => {
            this.checkChartUpdateSafety()
          }, 5000)
        }
        
        // CRITICAL: Clear any pending chart update timeout to prevent stuck loading state
        if (this.updateChartsTimeout) {
          console.log('🧹 Clearing updateChartsTimeout after chart creation')
          clearTimeout(this.updateChartsTimeout)
          this.updateChartsTimeout = null
        }
      }
    },
    */
  
  beforeMount() {
    console.log('🔧 Explorer beforeMount() - About to mount component')
    try {
      // Add any critical setup here
      console.log('🔧 Explorer beforeMount() - Basic setup complete')
      
      // Mark that we've reached beforeMount
      this._beforeMountReached = true
      
      // Force continue to mounted phase if stuck
      setTimeout(() => {
        if (!this._mountedPhaseStarted) {
          console.log('⚠️ Component stuck in beforeMount phase, forcing continue...')
          this._forceMounted = true
        }
      }, 1000)
      
    } catch (error) {
      console.error('❌ Error in Explorer beforeMount():', error)
    }
  },
  
  async mounted() {
    console.log('🚀 Explorer mounted - Starting structured initialization sequence')
    console.log('🔍 Initial component state:', {
      hasProcessingResults: !!this.processingResults,
      chartCreationComplete: this.chartCreationComplete,
      _componentInitialized: this._componentInitialized
    })
    
    // Hide back button - Explorer is typically opened in new tab/window
    // this.hideBackButton()
    
    // Set up flexible back button based on how user arrived
    this.setupBackButton()
    
    // Set up tab tracking and communication
    this.setupTabTracking()
    this.setupInterTabCommunication()
    
    // Set a flag to indicate we're in the mounted phase
    this._mountedPhaseStarted = true
    
    try {
      // PHASE 1: Data Validation + Full Dataset Timeline Range Fetch
      const dataValidationResult = await this.validateRequiredData()
      
      if (!dataValidationResult.isValid) {
        console.error('❌ Data validation failed:', dataValidationResult.errors)
        // Show error message to user
        return
      }
      
      // PHASE 2: Timeline Initialization - Set up timeline with validated data
      const timelineResult = await this.initializeTimeline()
      
      if (!timelineResult.success) {
        console.error('❌ Timeline initialization failed:', timelineResult.error)
        return
      }
      
      // PHASE 3: Data Fetching - Get the actual data for the timeline range
      const dataFetchResult = await this.fetchInitialData()
      
      if (!dataFetchResult.success) {
        console.error('❌ Data fetching failed:', dataFetchResult.error)
        return
      }
      
      // PHASE 4: Chart Creation - Create charts only after timeline and data are ready
      const chartResult = await this.createInitialCharts()
      
      if (!chartResult.success) {
        console.error('❌ Chart creation failed:', chartResult.error)
        return
      }
      
      // PHASE 5: Page Ready - Enable interactions only after everything is complete
      await this.setPageReady()
    } catch (error) {
      console.error('❌ Error during Explorer initialization:', error)
      console.error('❌ Stack trace:', error.stack)
    } finally {
      // Always mark component as initialized, even if there was an error
      this._componentInitialized = true
    }
  },
  
  beforeUnmount() {
      console.log('🗑️ Explorer beforeUnmount - Starting aggressive cleanup')
      
      // Clear time range data cache (Phase 1)
      this.timeRangeDataCache.clear()
      
      // CRITICAL: Set flag to prevent any new operations
      this._isUnmounting = true
      this._componentInitialized = false
      this.chartCreationComplete = false
      
      // Force stop all Chart.js operations immediately
      this.forceStopChartJS()
      
      // Aggressively destroy all chart components via refs
      if (this.$refs.signalChart) {
        try {
          console.log('🗑️ Destroying signal chart component')
          if (this.$refs.signalChart.destroyChart) {
            this.$refs.signalChart.destroyChart().catch(err => {
              console.warn('Error destroying signal chart:', err)
            })
          }
          // Get and destroy Chart.js instance directly
          const signalChartInstance = this.$refs.signalChart.getChart?.()
          if (signalChartInstance && signalChartInstance.destroy) {
            signalChartInstance.destroy()
          }
        } catch (error) {
          console.warn('Error destroying signal chart during unmount:', error)
        }
      }
      
      if (this.$refs.deviceChart) {
        try {
          console.log('🗑️ Destroying device chart component')
          if (this.$refs.deviceChart.destroyChart) {
            this.$refs.deviceChart.destroyChart().catch(err => {
              console.warn('Error destroying device chart:', err)
            })
          }
          // Get and destroy Chart.js instance directly
          const deviceChartInstance = this.$refs.deviceChart.getChart?.()
          if (deviceChartInstance && deviceChartInstance.destroy) {
            deviceChartInstance.destroy()
          }
        } catch (error) {
          console.warn('Error destroying device chart during unmount:', error)
        }
      }
      
      // Destroy legacy chart reference
      if (this.deviceChart) {
        try {
          if (typeof this.deviceChart.destroy === 'function') {
            this.deviceChart.destroy()
          }
        } catch (error) {
          console.warn('Error destroying legacy device chart during unmount:', error)
        }
        this.deviceChart = null
      }
      
      // Destroy ALL Chart.js instances globally
      if (window.Chart && window.Chart.instances) {
        Object.keys(window.Chart.instances).forEach(id => {
          try {
            const instance = window.Chart.instances[id]
            if (instance && typeof instance.destroy === 'function') {
              instance.destroy()
              console.log(`🗑️ Destroyed Chart.js instance ${id}`)
            }
          } catch (error) {
            console.warn(`Error destroying Chart.js instance ${id}:`, error)
          }
        })
      }
      
      // Clean up tab tracking
      this.cleanupTabTracking()
      
      // Clear ALL timeouts aggressively
      if (this.updateChartsTimeout) {
        clearTimeout(this.updateChartsTimeout)
        this.updateChartsTimeout = null
      }
      
      if (this.chartUpdateSafetyTimer) {
        clearTimeout(this.chartUpdateSafetyTimer)
        this.chartUpdateSafetyTimer = null
      }
      
      if (this.fetchDebounceTimeout) {
        clearTimeout(this.fetchDebounceTimeout)
        this.fetchDebounceTimeout = null
      }
      
      if (this.chartOperationTimeout) {
        clearTimeout(this.chartOperationTimeout)
        this.chartOperationTimeout = null
      }
      
      // Clear ALL intervals
      if (this.freezeStateCheckInterval) {
        clearInterval(this.freezeStateCheckInterval)
        this.freezeStateCheckInterval = null
      }
      
      if (this.chartHealthCheckInterval) {
        clearInterval(this.chartHealthCheckInterval)
        this.chartHealthCheckInterval = null
      }
      
      // Cancel any pending axios requests by resetting the cancel token source
      // Note: This requires axios cancel tokens to be implemented, but we'll reset state anyway
      this.isFetchingData = false
      
      // Remove ALL window event listeners
      window.removeEventListener('blur', this.handleWindowBlur)
      window.removeEventListener('focus', this.handleWindowFocus)
      window.removeEventListener('keydown', this.handleKeyDown)
      window.removeEventListener('visibilitychange', this.handleVisibilityChange)
      window.removeEventListener('beforeunload', this.handleBeforeUnload)
      
      // Remove chart error handlers if they were stored
      if (this._chartErrorHandler) {
        window.removeEventListener('error', this._chartErrorHandler)
        this._chartErrorHandler = null
      }
      if (this._chartRejectionHandler) {
        window.removeEventListener('unhandledrejection', this._chartRejectionHandler)
        this._chartRejectionHandler = null
      }
      
      // Remove ALL document event listeners
      document.removeEventListener('click', this.handleClickOutside)
      
      // Clean up resize handlers if active
      this.stopResizing()
      
      // Clean up any remaining tooltips
      this.cleanupTooltips()
      
      // Clean up custom tooltip handlers
      if (this.chartTooltipHandlers) {
        const { handleMouseMove, handleMouseLeave, canvas } = this.chartTooltipHandlers
        if (canvas) {
          canvas.removeEventListener('mousemove', handleMouseMove)
          canvas.removeEventListener('mouseleave', handleMouseLeave)
        }
        this.chartTooltipHandlers = null
      }
      
      // Reset ALL state flags to prevent any operations
      this.chartUpdateInProgress = false
      this.isUpdatingCharts = false
      this.pendingChartUpdate = false
      this.isFetchingData = false
      this._settingInitialTimeline = false
      this._updatingXAxis = false
      this._updatingCurveDatasets = false
      this._selectingCurve = false
      this._recoveringFromContextError = false
      this.isTimelineAdjusting = false
      this._checkboxChangeInProgress = false
      this._checkboxProcessingStatus = null
      
      // Clear reactive data to prevent watchers from firing
      this.currentTimeRangeData = null
      this.timelineStart = null
      this.timelineEnd = null
      this.datasetTimelineRange = null
      
      // Clear chart refs
      if (this.$refs.signalChart) {
        this.$refs.signalChart = null
      }
      if (this.$refs.deviceChart) {
        this.$refs.deviceChart = null
      }
      
      console.log('✅ Explorer beforeUnmount - Aggressive cleanup complete')
    },
    
    // Method to force reset all transitional state flags
    forceResetTransitionalState() {
      console.log('🔧 Force resetting all transitional state flags')
      
      // Clear any pending timeouts
      if (this.updateChartsTimeout) {
        clearTimeout(this.updateChartsTimeout)
        this.updateChartsTimeout = null
      }
      
      // Reset all transitional state flags
      this.isUpdatingCharts = false
      this.chartUpdateInProgress = false
      this.isFetchingData = false
      this._settingInitialTimeline = false
      this.pendingChartUpdate = false
      
      console.log('🔧 All transitional state flags reset')
    }
  }
}
</script>

<style scoped>
.timeline-pointer {
  transition: all 0.2s ease;
  /* Increase hit area with pseudo-element */
  position: relative;
}

.timeline-pointer::before {
  content: '';
  position: absolute;
  top: -4px;
  left: -4px;
  right: -4px;
  bottom: -4px;
  background: transparent;
  cursor: ew-resize;
}

.timeline-pointer:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.timeline-pointer.dragging {
  transform: scale(1.1);
  box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.4);
  z-index: 20;
}

.start-pointer.dragging {
  box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.4);
}

.end-pointer.dragging {
  box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.4);
}

/* Ensure both pointers are always visible and properly positioned */
.start-pointer {
  z-index: 15;
  position: absolute !important;
  top: 0 !important;
}

.end-pointer {
  z-index: 15;
  position: absolute !important;
  top: 0 !important;
}

/* Frozen pointer states */
.timeline-pointer.cursor-not-allowed {
  pointer-events: none;
  position: relative;
}

.timeline-pointer.cursor-not-allowed::after {
  content: '';
  position: absolute;
  top: -2px;
  left: -2px;
  right: -2px;
  bottom: -2px;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 0.125rem;
  pointer-events: none;
  z-index: 40;
  border: 2px solid rgba(156, 163, 175, 0.5);
}

/* Add a freeze indicator overlay */
.timeline-pointer.cursor-not-allowed::before {
  content: '⏸';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 12px;
  color: #6b7280;
  z-index: 41;
  pointer-events: none;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Ensure labels are always visible and properly positioned */
.timeline-pointer > div:last-child {
  z-index: 25;
  pointer-events: none;
  white-space: nowrap;
  min-width: max-content;
  overflow: visible;
  text-overflow: clip;
}

/* Ensure timeline container allows labels to overflow above */
.timeline-container {
  overflow: visible;
  margin-bottom: 1rem; /* Reduce bottom margin */
}

/* Timeline track allows labels above but clips content below the track */
.timeline-track {
  overflow: visible;
  position: relative;
  height: 48px; /* Explicit height constraint */
  background: linear-gradient(to right, #e5e7eb, #d1d5db);
}

/* Ensure pointers don't extend below the track */
.timeline-pointer {
  overflow: visible;
  border: 2px solid white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

/* Constrain pointer content to stay within track bounds */
.timeline-pointer > div {
  max-height: 48px; /* Match track height (h-12 = 48px) */
  overflow: hidden;
}

/* Ensure center line indicators are visible above the border and contained within pointer */
.timeline-pointer > div:first-child {
  z-index: 30;
  /* Ensure center line stays within pointer bounds */
  max-height: 100%;
  overflow: hidden;
}

.selected-range {
  background: linear-gradient(to right, #93c5fd, #3b82f6);
  box-shadow: inset 0 0 0 1px rgba(59, 130, 246, 0.3);
}

/* Custom scrollbar for timeline */
.timeline-track::-webkit-scrollbar {
  height: 8px;
}

.timeline-track::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.timeline-track::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.timeline-track::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Curve tooltip styles */
.curve-tooltip {
  position: absolute;
  background: rgba(0, 0, 0, 0.9);
  color: white;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  pointer-events: none;
  z-index: 1000;
  white-space: nowrap;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(4px);
  transition: opacity 0.2s ease-in-out;
}

/* Checkbox tooltip styles */
.checkbox-tooltip {
  animation: fadeIn 0.2s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.curve-tooltip::before {
  content: '';
  position: absolute;
  top: -4px;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-bottom: 4px solid rgba(0, 0, 0, 0.9);
}

/* Loading dots animation */
@keyframes dotAnimation {
  0%, 20% {
    content: '';
  }
  40% {
    content: '.';
  }
  60% {
    content: '..';
  }
  80%, 100% {
    content: '...';
  }
}

.loading-dots::after {
  content: '';
  animation: dotAnimation 1.5s steps(4, end) infinite;
}
</style>


